// src/Stats.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Divider,
  Chip,
  Avatar,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  LinearProgress,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Switch,
  FormControlLabel,
  Badge
} from '@mui/material';
import { signOut } from 'firebase/auth';
import { collection, query, orderBy, getDocs, where, getDoc, doc } from 'firebase/firestore';
import { auth, db } from './firebase';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, isValid, subMonths, startOfMonth, endOfMonth, eachMonthOfInterval } from 'date-fns';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, 
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
  Legend, ResponsiveContainer, Cell, Scatter, ScatterChart, 
  ZAxis, Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, 
  ComposedChart, Area
} from 'recharts';
import { safeFormatDate } from './TradeForm';

// Material Icons
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import TimelineIcon from '@mui/icons-material/Timeline';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import DateRangeIcon from '@mui/icons-material/DateRange';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InsightsIcon from '@mui/icons-material/Insights';
import WaterfallChartIcon from '@mui/icons-material/WaterfallChart';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import PresentToAllIcon from '@mui/icons-material/PresentToAll';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import SummarizeIcon from '@mui/icons-material/Summarize';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import MoneyIcon from '@mui/icons-material/Money';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import SpeedIcon from '@mui/icons-material/Speed';
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
// הוסף את השורות הבאות בין ייבואי האייקונים האחרים בתחילת הקובץ
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import GroupIcon from '@mui/icons-material/Group';
import CompareIcon from '@mui/icons-material/Compare';
import PersonIcon from '@mui/icons-material/Person';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FilterListIcon from '@mui/icons-material/FilterList';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import TimerIcon from '@mui/icons-material/Timer';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import AlarmIcon from '@mui/icons-material/Alarm';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';

const COLORS = [
  '#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F', 
  '#FFBB28', '#FF8042', '#a4de6c', '#d0ed57', '#83a6ed', '#8dd1e1',
  '#a4c2f4', '#9575cd', '#b39ddb', '#e57373', '#f48fb1', '#ce93d8',
  '#9fa8da', '#90caf9', '#81d4fa', '#80deea', '#80cbc4', '#a5d6a7',
  '#c5e1a5', '#e6ee9c', '#fff59d', '#ffe082', '#ffcc80', '#ffab91'
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

// פונקציה לפורמט של משך הזמן בימים, שעות, דקות
const formatDuration = (minutes) => {
  if (!minutes || isNaN(minutes)) return '0 דקות';
  
  const days = Math.floor(minutes / (24 * 60));
  const hours = Math.floor((minutes % (24 * 60)) / 60);
  const mins = Math.floor(minutes % 60);
  
  let result = '';
  if (days > 0) result += `${days} י' `;
  if (hours > 0) result += `${hours} ש' `;
  if (mins > 0 || (days === 0 && hours === 0)) result += `${mins} ד'`;
  
  return result.trim();
};

const Stats = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const user = auth.currentUser;

  // State
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [timeFilter, setTimeFilter] = useState('all');
  const [symbolFilter, setSymbolFilter] = useState('all');
  
  // Admin panel state
  const [isAdmin, setIsAdmin] = useState(true); // Assuming admin access for now
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userDetails, setUserDetails] = useState({});
  const [isCompareMode, setIsCompareMode] = useState(false);
  const [adminPanelExpanded, setAdminPanelExpanded] = useState(true);
  const [userTriggerFilter, setUserTriggerFilter] = useState('all');
  const [availableTriggers, setAvailableTriggers] = useState([]);
  const [stopReasonDialogOpen, setStopReasonDialogOpen] = useState(false);
  const [selectedStopReason, setSelectedStopReason] = useState(null);
  const [detailTrade, setDetailTrade] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [imageDialogUrl, setImageDialogUrl] = useState('');
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  // מצב חדש עבור דיאלוג win/loss breakdown
  const [outcomeDialogOpen, setOutcomeDialogOpen] = useState(false);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  // מצב חדש עבור פילטר TP Milestone
  const [tpMilestoneFilter, setTpMilestoneFilter] = useState('all');
  const [availableTpMilestones, setAvailableTpMilestones] = useState(['all']);

  // חישוב מדד העקביות על בסיס סטיית תקן
  const calculateConsistencyScore = (trades) => {
    if (!trades || trades.length === 0) return 0;
    
    // מחשבים את ממוצע ה-R
    const avgR = trades.reduce((sum, trade) => sum + trade.r, 0) / trades.length;
    
    // מחשבים את סטיית התקן
    const variance = trades.reduce((sum, trade) => sum + Math.pow(trade.r - avgR, 2), 0) / trades.length;
    const stdDev = Math.sqrt(variance);
    
    // מחשבים את מקדם השונות (CV)
    const cv = Math.abs(stdDev / avgR);
    
    // ממירים את ה-CV למדד עקביות (יחס הפוך)
    // ככל שה-CV נמוך יותר, העקביות גבוהה יותר
    // מסקילים מ-0 עד 100
    const consistencyScore = Math.max(0, Math.min(100, 100 - (cv * 50)));
    
    return Math.round(consistencyScore);
  };

  // פונקציות לפתיחת דיאלוגים שונים
  const openImageDialog = (url) => {
    setImageDialogUrl(url);
    setImageDialogOpen(true);
  };

  const handleDetailClick = (trade) => {
    setDetailTrade(trade);
    setDetailDialogOpen(true);
  };

  // פונקציה חדשה לטיפול בלחיצה על סגמנט win/loss בעוגה
  const handleOutcomeClick = (outcome) => {
    setSelectedOutcome(outcome);
    setOutcomeDialogOpen(true);
  };

  const handleStopReasonClick = (reason) => {
    setSelectedStopReason(reason);
    setStopReasonDialogOpen(true);
  };

  // Fetch all users for admin panel
  useEffect(() => {
    const fetchUsers = async () => {
      if (!isAdmin) return;

      try {
        const usersRef = collection(db, 'users');
        const usersSnapshot = await getDocs(usersRef);
        const usersData = [];
        const userDetailsData = {};
        const allTriggers = new Set();

        usersSnapshot.forEach((doc) => {
          const userData = doc.data();
          usersData.push({
            id: doc.id,
            userName: userData.userName || userData.email.split('@')[0],
            email: userData.email,
            tradingHours: userData.tradingHours || "לא מוגדר",
            trigger: userData.trigger || "לא מוגדר",
          });
          
          userDetailsData[doc.id] = userData;
          
          if (userData.trigger) {
            allTriggers.add(userData.trigger);
          }
        });

        setAllUsers(usersData);
        setUserDetails(userDetailsData);
        setAvailableTriggers(['all', ...Array.from(allTriggers)]);
        
        // If we haven't selected any users yet, start with the current user
        if (selectedUsers.length === 0 && user) {
          setSelectedUsers([user.uid]);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchUsers();
  }, [isAdmin, user]);

  // Fetch trades data
  useEffect(() => {
    const fetchTrades = async () => {
      try {
        setLoading(true);
        
        // Determine which users to fetch trades for
        const usersToFetch = isAdmin && selectedUsers.length > 0 ? selectedUsers : (user ? [user.uid] : []);
        
        if (usersToFetch.length === 0) {
          setTrades([]);
          setLoading(false);
          return;
        }
        
        console.log("Fetching trades for users:", usersToFetch);
        
        const tradesData = [];
        
        // If there are multiple users to fetch, we need to do multiple queries
        for (const userId of usersToFetch) {
          const tradesRef = collection(db, 'trades');
          const q = query(
            tradesRef, 
            where('uid', '==', userId)
          );
          
          const querySnapshot = await getDocs(q);
          
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            const userName = userDetails[userId]?.userName || userId;
            
            tradesData.push({
              id: doc.id,
              ...data,
              userName: userName,
              userDisplayName: allUsers.find(u => u.id === userId)?.userName || userName,
              parsedDate: data.date ? parseISO(data.date) : new Date(),
              triggerAppeared: data.triggerAppeared || 'no',
              session: data.session || 'לא מוגדר',
              matchedTrigger: data.matchedTrigger || 'לא מוגדר',
              tpMilestoneExit: data.tpMilestoneExit || '-1R',
              duration: data.duration || 0,
              stopReason: data.stopReason || 'לא צוין',
              dayOfWeek: data.dayOfWeek || 'לא מוגדר'
            });
          });
        }
        
        console.log("Processed trades count:", tradesData.length);
        setTrades(tradesData);

        // אוספים את ערכי TP Milestone הזמינים מתוך העסקאות
        const tpMilestones = new Set();
        tradesData.forEach(trade => {
          if (trade.tpMilestones && Array.isArray(trade.tpMilestones)) {
            trade.tpMilestones.forEach(milestone => {
              if (milestone && milestone.tp) {
                tpMilestones.add(milestone.tp);
              }
            });
          }
          // מוסיפים גם את ערך tpMilestoneExit, אם הוא קיים
          if (trade.tpMilestoneExit && trade.tpMilestoneExit !== '-1R') {
            tpMilestones.add(trade.tpMilestoneExit);
          }
        });
        
        setAvailableTpMilestones(['all', ...Array.from(tpMilestones)]);
        
      } catch (error) {
        console.error("Error fetching trades:", error);
        setTrades([]); // אתחל מערך ריק בעת שגיאה
      } finally {
        setLoading(false);
      }
    };
    
    fetchTrades();
  }, [user, selectedUsers, isAdmin, userDetails, allUsers]);

  // חישוב תאריכי המסחר
  const tradeDates = useMemo(() => {
    if (trades.length === 0) return [];
    
    // מקבלים תאריכי מסחר ייחודיים
    const uniqueDates = new Set();
    trades.forEach(trade => {
      if (trade.date) {
        uniqueDates.add(trade.date);
      }
    });
    
    return Array.from(uniqueDates);
  }, [trades]);

  // טווח תאריכים (בימים)
  const tradeDatesRange = useMemo(() => {
    if (tradeDates.length <= 1) return tradeDates.length;
    
    // מוצאים את התאריך המוקדם ביותר והמאוחר ביותר
    const dates = tradeDates.map(dateStr => new Date(dateStr));
    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));
    
    // מחשבים את ההפרש בימים
    const diffTime = Math.abs(maxDate - minDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 כדי לכלול גם את היום הראשון
    
    return diffDays;
  }, [tradeDates]);

  // Filtered trades based on all filters
  const filteredTrades = useMemo(() => {
    let filtered = [...trades];
    
    // Apply time filter
    if (timeFilter === 'last30') {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      filtered = filtered.filter(trade => isValid(trade.parsedDate) && trade.parsedDate >= thirtyDaysAgo);
    } else if (timeFilter === 'last90') {
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
      filtered = filtered.filter(trade => isValid(trade.parsedDate) && trade.parsedDate >= ninetyDaysAgo);
    } else if (timeFilter === 'thisYear') {
      const startOfYear = new Date(new Date().getFullYear(), 0, 1);
      filtered = filtered.filter(trade => isValid(trade.parsedDate) && trade.parsedDate >= startOfYear);
    }
    
    // Apply symbol filter
    if (symbolFilter !== 'all') {
      filtered = filtered.filter(trade => trade.symbol === symbolFilter);
    }
    
    // Apply trigger filter based on trade.matchedTrigger field
    if (userTriggerFilter !== 'all') {
      filtered = filtered.filter(trade => trade.matchedTrigger === userTriggerFilter);
    }
    
    return filtered;
  }, [trades, timeFilter, symbolFilter, userTriggerFilter]);

  // Get all trades for analysis (including those without trigger)
  const realTrades = useMemo(() => {
    return filteredTrades.filter(trade => trade.triggerAppeared === 'yes');
  }, [filteredTrades]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (realTrades.length === 0) {
      return {
        totalR: 0,
        avgR: 0,
        winRate: 0,
        totalTrades: 0,
        winCount: 0,
        lossCount: 0,
        bestR: 0,
        worstR: 0,
        profitFactor: 0,
        expectancy: 0,
        bestDay: null,
        worstDay: null,
        averageDuration: 0,
      };
    }

    // Helper function to calculate R value
    const getTradeR = (trade) => {
      if (trade.outcome === 'Stop Loss') return -1;
      
      if (trade.outcome === 'Take Profit') {
        // Check for "point of exit"
        if (trade.tpMilestoneExit) {
          const parts = trade.tpMilestoneExit.split(':');
          if (parts.length === 2) {
            return parseFloat(parts[1]);
          }
        }
        
        // If no point of exit found, return 1
        return 1;
      }
      
      return 0;
    };

    // Calculate basic stats
    const totalR = realTrades.reduce((acc, trade) => acc + getTradeR(trade), 0);
    const winCount = realTrades.filter(t => t.outcome === 'Take Profit').length;
    const lossCount = realTrades.filter(t => t.outcome === 'Stop Loss').length;
    const winRate = (winCount / realTrades.length) * 100;
    
    // Calculate R values for each trade
    const rValues = realTrades.map(trade => ({
      ...trade,
      r: getTradeR(trade)
    }));
    
    // Get best and worst R
    const bestR = Math.max(...rValues.map(t => t.r));
    const worstR = Math.min(...rValues.map(t => t.r));
    
    // Calculate profit factor
    const grossProfit = rValues.filter(t => t.r > 0).reduce((acc, t) => acc + t.r, 0);
    const grossLoss = Math.abs(rValues.filter(t => t.r < 0).reduce((acc, t) => acc + t.r, 0));
    const profitFactor = grossLoss === 0 ? grossProfit : grossProfit / grossLoss;
    
    // Calculate expectancy
    const expectancy = (winRate / 100) * (grossProfit / winCount || 0) - ((100 - winRate) / 100) * (grossLoss / lossCount || 0);
    
    // Group trades by date
    const tradesByDate = {};
    rValues.forEach(trade => {
      if (!tradesByDate[trade.date]) {
        tradesByDate[trade.date] = [];
      }
      tradesByDate[trade.date].push(trade);
    });
    
    // Calculate daily R
    const dailyR = Object.keys(tradesByDate).map(date => ({
      date,
      r: tradesByDate[date].reduce((acc, trade) => acc + trade.r, 0)
    }));
    
    // Get best and worst day
    const bestDay = dailyR.reduce((best, current) => !best || current.r > best.r ? current : best, null);
    const worstDay = dailyR.reduce((worst, current) => !worst || current.r < worst.r ? current : worst, null);
    
    // Calculate average duration
    const totalDuration = realTrades.reduce((acc, trade) => acc + (parseInt(trade.duration) || 0), 0);
    const averageDuration = totalDuration / realTrades.length;
    
    return {
      totalR: totalR,
      avgR: totalR / realTrades.length,
      winRate,
      totalTrades: realTrades.length,
      winCount,
      lossCount,
      bestR,
      worstR,
      profitFactor,
      expectancy,
      bestDay,
      worstDay,
      averageDuration,
      rValues,
      dailyR
    };
  }, [realTrades]);

  // Simulated stats based on TP milestone filter
  const simulatedStats = useMemo(() => {
    if (realTrades.length === 0 || tpMilestoneFilter === 'all') {
      return {
        totalR: stats.totalR,
        avgR: stats.avgR,
        winRate: stats.winRate,
        totalTrades: stats.totalTrades,
        winCount: stats.winCount,
        lossCount: stats.lossCount,
        profitFactor: stats.profitFactor,
        expectancy: stats.expectancy,
      };
    }

    // We'll simulate what would happen if all trades used the selected TP milestone
    const targetTP = tpMilestoneFilter;
    
    // Extract the R value from the TP milestone
    let targetR = 1; // Default
    const parts = targetTP.split(':');
    if (parts.length === 2) {
      targetR = parseFloat(parts[1]);
    }
    
    // Simulate trades with this TP
// Simulate trades with this TP
const simulatedTrades = realTrades.map(trade => {
  // Check if this trade has reached the target TP ratio or higher
  let hasReachedTarget = false;
  
  if (trade.tpMilestones && Array.isArray(trade.tpMilestones)) {
    hasReachedTarget = trade.tpMilestones.some(milestone => {
      // Check if this milestone was reached
      if (!milestone.reached) return false;
      
      // Parse the milestone's TP value
      const milestoneParts = milestone.tp.split(':');
      if (milestoneParts.length !== 2) return false;
      
      // Get the R value of this milestone
      const milestoneR = parseFloat(milestoneParts[1]);
      
      // Check if this milestone's R value is >= our target R
      return !isNaN(milestoneR) && milestoneR >= targetR;
    });
  }
  
  return {
    ...trade,
    simulatedOutcome: hasReachedTarget ? 'Take Profit' : 'Stop Loss',
    simulatedR: hasReachedTarget ? targetR : -1
  };
});    
    // Calculate simulated stats
    const simWinCount = simulatedTrades.filter(t => t.simulatedOutcome === 'Take Profit').length;
    const simLossCount = simulatedTrades.filter(t => t.simulatedOutcome === 'Stop Loss').length;
    const simWinRate = (simWinCount / simulatedTrades.length) * 100;
    
    const simTotalR = simulatedTrades.reduce((acc, trade) => acc + trade.simulatedR, 0);
    const simAvgR = simTotalR / simulatedTrades.length;
    
    // Calculate profit factor
    const simGrossProfit = simulatedTrades.filter(t => t.simulatedR > 0).reduce((acc, t) => acc + t.simulatedR, 0);
    const simGrossLoss = Math.abs(simulatedTrades.filter(t => t.simulatedR < 0).reduce((acc, t) => acc + t.simulatedR, 0));
    const simProfitFactor = simGrossLoss === 0 ? simGrossProfit : simGrossProfit / simGrossLoss;
    
    // Calculate expectancy
    const simExpectancy = (simWinRate / 100) * (simGrossProfit / simWinCount || 0) - 
                        ((100 - simWinRate) / 100) * (simGrossLoss / simLossCount || 0);
    
    return {
      totalR: simTotalR,
      avgR: simAvgR,
      winRate: simWinRate,
      totalTrades: simulatedTrades.length,
      winCount: simWinCount,
      lossCount: simLossCount,
      profitFactor: simProfitFactor,
      expectancy: simExpectancy,
      simulatedTrades
    };
  }, [realTrades, tpMilestoneFilter, stats]);

  // Calculate per-user statistics (for admin mode)
  const userStats = useMemo(() => {
    if (!isAdmin || realTrades.length === 0) return [];

    // Group trades by user
    const tradesByUser = {};
    
    realTrades.forEach(trade => {
      const userId = trade.uid;
      if (!tradesByUser[userId]) {
        tradesByUser[userId] = [];
      }
      tradesByUser[userId].push(trade);
    });

    // Helper function to calculate R value
    const getTradeR = (trade) => {
      if (trade.outcome === 'Stop Loss') return -1;
      
      if (trade.outcome === 'Take Profit') {
        if (trade.tpMilestoneExit) {
          const parts = trade.tpMilestoneExit.split(':');
          if (parts.length === 2) {
            return parseFloat(parts[1]);
          }
        }
        return 1;
      }
      
      return 0;
    };

    // Calculate stats for each user
    return Object.keys(tradesByUser).map(userId => {
      const userTrades = tradesByUser[userId];
      const userName = userTrades[0]?.userDisplayName || 'לא ידוע';
      const email = userTrades[0]?.email || allUsers.find(u => u.id === userId)?.email || 'לא ידוע';
      const trigger = allUsers.find(u => u.id === userId)?.trigger || 'לא ידוע';
      const tradingHours = allUsers.find(u => u.id === userId)?.tradingHours || 'לא ידוע';
      
      // Calculate basic stats
      const totalR = userTrades.reduce((acc, trade) => acc + getTradeR(trade), 0);
      const winCount = userTrades.filter(t => t.outcome === 'Take Profit').length;
      const lossCount = userTrades.filter(t => t.outcome === 'Stop Loss').length;
      const winRate = (winCount / userTrades.length) * 100;
      
      return {
        userId,
        userName,
        email,
        trigger,
        tradingHours,
        totalTrades: userTrades.length,
        totalR,
        avgR: totalR / userTrades.length,
        winRate,
        winCount,
        lossCount
      };
    }).sort((a, b) => b.avgR - a.avgR); // Sort by average R
  }, [isAdmin, realTrades, allUsers]);

  // Prepare data for charts
  const chartsData = useMemo(() => {
    // אם אין עסקאות, החזר אובייקט עם מערכים ריקים
    if (realTrades.length === 0) {
      return {
        cumulativeR: [], 
        rByDay: [],
        winRateByDay: [],
        rBySymbol: [],
        rBySession: [],
        rByTrigger: [],
        rByReport: [],
        rByTimeDistribution: [],
        durationVsR: [],
        monthlyPerformance: [],
        successRateByTimeframe: [],
        tpDistribution: [],
        stopReasons: [],
        marketPhasePerformance: [],
        rByUser: [],  // New chart for admin mode
        stopsByDay: [] // New chart for stop losses by day
      };
    }

    // Helper function to calculate R value
    const getTradeR = (trade) => {
      if (trade.outcome === 'Stop Loss') return -1;
      
      if (trade.outcome === 'Take Profit') {
        // Check for "point of exit"
        if (trade.tpMilestoneExit) {
          const parts = trade.tpMilestoneExit.split(':');
          if (parts.length === 2) {
            return parseFloat(parts[1]);
          }
        }
        
        // If no point of exit found, return 1
        return 1;
      }
      
      return 0;
    };

    // Prepare cumulative R chart data with a different approach
    const computedCumulativeR = [];
    let runningTotal = 0;

    for (let i = 0; i < realTrades.length; i++) {
      const trade = realTrades[i];
      const r = getTradeR(trade);
      runningTotal += r;

      computedCumulativeR.push({
        tradeNumber: i + 1,
        date: trade.date,
        r: r,
        cumulative: runningTotal,
        symbol: trade.symbol,
        session: trade.session,
        outcome: trade.outcome,
        userId: trade.uid,
        userName: trade.userDisplayName || trade.userName
      });
    }

    // Prepare R by day of week chart data
    const rByDayMap = {};
    const countByDayMap = {};
    const winsByDayMap = {};
    const stopsByDayMap = {}; // מבנה נתונים חדש לעקיבה אחר סטופים לפי יום
    
    realTrades.forEach(trade => {
      const dayOfWeek = trade.dayOfWeek;
      const r = getTradeR(trade);
      
      if (!rByDayMap[dayOfWeek]) {
        rByDayMap[dayOfWeek] = 0;
        countByDayMap[dayOfWeek] = 0;
        winsByDayMap[dayOfWeek] = 0;
        stopsByDayMap[dayOfWeek] = 0; // אתחול ספירת סטופים ליום זה
      }
      
      rByDayMap[dayOfWeek] += r;
      countByDayMap[dayOfWeek]++;
      
      if (trade.outcome === 'Take Profit') {
        winsByDayMap[dayOfWeek]++;
      } else if (trade.outcome === 'Stop Loss') {
        stopsByDayMap[dayOfWeek]++; // ספירת סטופים לפי יום
      }
    });
    
    const daysOrder = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
    
    const rByDay = daysOrder.map(day => ({
      day,
      r: rByDayMap[day] || 0,
      count: countByDayMap[day] || 0,
      avgR: countByDayMap[day] ? (rByDayMap[day] / countByDayMap[day]) : 0
    }));
    
    const winRateByDay = daysOrder.map(day => ({
      day,
      winRate: countByDayMap[day] ? (winsByDayMap[day] / countByDayMap[day]) * 100 : 0,
      totalTrades: countByDayMap[day] || 0
    }));

    // חדש: נתוני תרשים הסטופים לפי יום
    const stopsByDay = daysOrder.map(day => ({
      day,
      stops: stopsByDayMap[day] || 0,
      totalTrades: countByDayMap[day] || 0,
      stopsPercentage: countByDayMap[day] ? (stopsByDayMap[day] / countByDayMap[day]) * 100 : 0
    }));

    // Prepare R by symbol chart data
    const rBySymbolMap = {};
    const countBySymbolMap = {};
    
    realTrades.forEach(trade => {
      const symbol = trade.symbol;
      const r = getTradeR(trade);
      
      if (!rBySymbolMap[symbol]) {
        rBySymbolMap[symbol] = 0;
        countBySymbolMap[symbol] = 0;
      }
      
      rBySymbolMap[symbol] += r;
      countBySymbolMap[symbol]++;
    });
    
    const rBySymbol = Object.keys(rBySymbolMap).map(symbol => ({
      symbol,
      r: rBySymbolMap[symbol],
      count: countBySymbolMap[symbol],
      avgR: rBySymbolMap[symbol] / countBySymbolMap[symbol]
    }));

    // Prepare R by session chart data
    const rBySessionMap = {};
    const countBySessionMap = {};
    
    realTrades.forEach(trade => {
      const session = trade.session;
      const r = getTradeR(trade);
      
      if (!rBySessionMap[session]) {
        rBySessionMap[session] = 0;
        countBySessionMap[session] = 0;
      }
      
      rBySessionMap[session] += r;
      countBySessionMap[session]++;
    });
    
    const rBySession = Object.keys(rBySessionMap).map(session => ({
      session,
      r: rBySessionMap[session],
      count: countBySessionMap[session],
      avgR: rBySessionMap[session] / countBySessionMap[session]
    }));

    // Prepare R by trigger chart data
    const rByTriggerMap = {};
    const countByTriggerMap = {};
    
    realTrades.forEach(trade => {
      const trigger = trade.matchedTrigger;
      const r = getTradeR(trade);
      
      if (!rByTriggerMap[trigger]) {
        rByTriggerMap[trigger] = 0;
        countByTriggerMap[trigger] = 0;
      }
      
      rByTriggerMap[trigger] += r;
      countByTriggerMap[trigger]++;
    });
    
    const rByTrigger = Object.keys(rByTriggerMap).map(trigger => ({
      trigger,
      r: rByTriggerMap[trigger],
      count: countByTriggerMap[trigger],
      avgR: rByTriggerMap[trigger] / countByTriggerMap[trigger]
    }));

    // Prepare R by report chart data
    const rByReportMap = {};
    const countByReportMap = {};
    
    realTrades.forEach(trade => {
      const report = trade.report || 'לא הוגדר';
      const r = getTradeR(trade);
      
      if (!rByReportMap[report]) {
        rByReportMap[report] = 0;
        countByReportMap[report] = 0;
      }
      
      rByReportMap[report] += r;
      countByReportMap[report]++;
    });
    
    const rByReport = Object.keys(rByReportMap)
      .filter(report => countByReportMap[report] >= 3) // Only include reports with at least 3 trades
      .map(report => ({
        report,
        r: rByReportMap[report],
        count: countByReportMap[report],
        avgR: rByReportMap[report] / countByReportMap[report]
      }))
      .sort((a, b) => b.avgR - a.avgR); // Sort by average R

    // Prepare R by user data (for admin mode)
    const rByUserMap = {};
    const countByUserMap = {};
    const winsByUserMap = {};
    
    realTrades.forEach(trade => {
      const userId = trade.uid;
      const userName = trade.userDisplayName || trade.userName;
      const userKey = `${userName} (${userId})`;
      const r = getTradeR(trade);
      
      if (!rByUserMap[userKey]) {
        rByUserMap[userKey] = 0;
        countByUserMap[userKey] = 0;
        winsByUserMap[userKey] = 0;
      }
      
      rByUserMap[userKey] += r;
      countByUserMap[userKey]++;
      
      if (trade.outcome === 'Take Profit') {
        winsByUserMap[userKey]++;
      }
    });
    
    const rByUser = Object.keys(rByUserMap).map(userKey => ({
      user: userKey.split(' (')[0], // Just the name part
      userId: userKey.split('(')[1].replace(')', ''), // Just the ID part
      r: rByUserMap[userKey],
      count: countByUserMap[userKey],
      avgR: rByUserMap[userKey] / countByUserMap[userKey],
      winRate: (winsByUserMap[userKey] / countByUserMap[userKey]) * 100
    })).sort((a, b) => b.avgR - a.avgR);

    // Prepare R by time distribution chart data (כעת עם דיוק של דקות)
    const rByTimeMap = {};
    const countByTimeMap = {};
    
    realTrades.forEach(trade => {
      if (!trade.time) return;
      
      // השתמש בזמן המלא (HH:MM) במקום רק בשעה
      const timeKey = trade.time;
      const r = getTradeR(trade);
      
      if (!rByTimeMap[timeKey]) {
        rByTimeMap[timeKey] = 0;
        countByTimeMap[timeKey] = 0;
      }
      
      rByTimeMap[timeKey] += r;
      countByTimeMap[timeKey]++;
    });
    
    const rByTimeDistribution = Object.keys(rByTimeMap)
      .sort((a, b) => {
        // מיון לפי שעות ואז דקות
        const aTime = a.split(':');
        const bTime = b.split(':');
        const aHour = parseInt(aTime[0] || 0);
        const bHour = parseInt(bTime[0] || 0);
        
        if (aHour !== bHour) return aHour - bHour;
        
        const aMinute = parseInt(aTime[1] || 0);
        const bMinute = parseInt(bTime[1] || 0);
        return aMinute - bMinute;
      })
      .map(timeKey => ({
        time: timeKey,
        r: rByTimeMap[timeKey],
        count: countByTimeMap[timeKey],
        avgR: rByTimeMap[timeKey] / countByTimeMap[timeKey]
      }));

    // Prepare duration vs R scatter chart data
    const durationVsR = realTrades
      .filter(trade => trade.duration)
      .map(trade => ({
        duration: parseInt(trade.duration),
        r: getTradeR(trade),
        outcome: trade.outcome,
        symbol: trade.symbol,
        userId: trade.uid,
        userName: trade.userDisplayName || trade.userName
      }));

    // Prepare monthly performance data
    const tradeDates = realTrades.map(trade => new Date(trade.date));
    const firstTradeDate = new Date(Math.min(...tradeDates));
    const lastTradeDate = new Date(Math.max(...tradeDates));
        
    const monthlyRMap = {};
    const monthlyTradeCountMap = {};
    const monthlyWinCountMap = {};
    
    realTrades.forEach(trade => {
      const date = new Date(trade.date);
      const monthKey = safeFormatDate(date, 'yyyy-MM');
      const r = getTradeR(trade);
      
      if (!monthlyRMap[monthKey]) {
        monthlyRMap[monthKey] = 0;
        monthlyTradeCountMap[monthKey] = 0;
        monthlyWinCountMap[monthKey] = 0;
      }
      
      monthlyRMap[monthKey] += r;
      monthlyTradeCountMap[monthKey]++;
      
      if (trade.outcome === 'Take Profit') {
        monthlyWinCountMap[monthKey]++;
      }
    });
    
    // Generate all months in range
    const allMonths = eachMonthOfInterval({
      start: startOfMonth(firstTradeDate),
      end: endOfMonth(lastTradeDate)
    });
    
    const monthlyPerformance = allMonths.map(date => {
      const monthKey = safeFormatDate(date, 'yyyy-MM');
      const monthName = safeFormatDate(date, 'MMM yyyy');
      const r = monthlyRMap[monthKey] || 0;
      const count = monthlyTradeCountMap[monthKey] || 0;
      const winCount = monthlyWinCountMap[monthKey] || 0;
      
      return {
        month: monthName,
        monthKey,
        r,
        count,
        avgR: count ? r / count : 0,
        winRate: count ? (winCount / count) * 100 : 0
      };
    });

    // Prepare success rate by timeframe data
    const successRateByTimeframeMap = {
      mainTimeframe: {},
      sessionChannelTimeframe: {},
      triggerIdentificationTimeframe: {},
      entryConfirmationTimeframe: {}
    };
    
    const countByTimeframeMap = {
      mainTimeframe: {},
      sessionChannelTimeframe: {},
      triggerIdentificationTimeframe: {},
      entryConfirmationTimeframe: {}
    };
    
    const winsByTimeframeMap = {
      mainTimeframe: {},
      sessionChannelTimeframe: {},
      triggerIdentificationTimeframe: {},
      entryConfirmationTimeframe: {}
    };
    
    realTrades.forEach(trade => {
      ['mainTimeframe', 'sessionChannelTimeframe', 'triggerIdentificationTimeframe', 'entryConfirmationTimeframe'].forEach(timeframeType => {
        const timeframe = trade[timeframeType];
        if (!timeframe) return;
        
        if (!countByTimeframeMap[timeframeType][timeframe]) {
          countByTimeframeMap[timeframeType][timeframe] = 0;
          winsByTimeframeMap[timeframeType][timeframe] = 0;
        }
        
        countByTimeframeMap[timeframeType][timeframe]++;
        
        if (trade.outcome === 'Take Profit') {
          winsByTimeframeMap[timeframeType][timeframe]++;
        }
      });
    });
    
    const successRateByTimeframe = [];
    
    ['mainTimeframe', 'sessionChannelTimeframe', 'triggerIdentificationTimeframe', 'entryConfirmationTimeframe'].forEach(timeframeType => {
      Object.keys(countByTimeframeMap[timeframeType]).forEach(timeframe => {
        const count = countByTimeframeMap[timeframeType][timeframe];
        const wins = winsByTimeframeMap[timeframeType][timeframe];
        
        successRateByTimeframe.push({
          timeframeType: timeframeType === 'mainTimeframe' ? 'טווח ראשי' :
                          timeframeType === 'sessionChannelTimeframe' ? 'תעלה סשן' :
                          timeframeType === 'triggerIdentificationTimeframe' ? 'זיהוי טריגר' :
                          'אישור כניסה',
          timeframe,
          count,
          successRate: count ? (wins / count) * 100 : 0
        });
      });
    });

    // Prepare TP distribution data
    const tpDistributionMap = {};
    
    realTrades.forEach(trade => {
      if (!trade.tpMilestones) return;
      
      const exitMilestone = trade.tpMilestones.find(m => m.status === 'יציאה');
      if (!exitMilestone) return;
      
      const tp = exitMilestone.tp;
      
      if (!tpDistributionMap[tp]) {
        tpDistributionMap[tp] = 0;
      }
      
      tpDistributionMap[tp]++;
    });
    
    const tpDistribution = Object.keys(tpDistributionMap)
      .map(tp => ({
        tp,
        count: tpDistributionMap[tp]
      }))
      .sort((a, b) => {
        if (a.tp === "-1R") return 1;
        if (b.tp === "-1R") return -1;
        
        const valueA = parseFloat(a.tp.split(':')[1] || 0);
        const valueB = parseFloat(b.tp.split(':')[1] || 0);
        
        return valueA - valueB;
      });

    // Prepare stop reasons data
    const stopReasonsMap = {};
    
    realTrades
      .filter(trade => trade.outcome === 'Stop Loss')
      .forEach(trade => {
        const reason = trade.stopReason || 'לא צוין';
        
        if (!stopReasonsMap[reason]) {
          stopReasonsMap[reason] = 0;
        }
        
        stopReasonsMap[reason]++;
      });
    
    const stopReasons = Object.keys(stopReasonsMap).map(reason => ({
      reason,
      count: stopReasonsMap[reason]
    }));

    // Prepare market phase performance data
    const marketPhaseMap = {
      marketSeason: {},
      weeklyWavePhase: {},
      dailyWavePhase: {}
    };
    
    const marketPhaseCountMap = {
      marketSeason: {},
      weeklyWavePhase: {},
      dailyWavePhase: {}
    };
    
    realTrades.forEach(trade => {
      ['marketSeason', 'weeklyWavePhase', 'dailyWavePhase'].forEach(phaseType => {
        const phase = trade[phaseType];
        if (!phase) return;
        
        const r = getTradeR(trade);
        
        if (!marketPhaseMap[phaseType][phase]) {
          marketPhaseMap[phaseType][phase] = 0;
          marketPhaseCountMap[phaseType][phase] = 0;
        }
        
        marketPhaseMap[phaseType][phase] += r;
        marketPhaseCountMap[phaseType][phase]++;
      });
    });
    
    const marketPhasePerformance = [];
    
    ['marketSeason', 'weeklyWavePhase', 'dailyWavePhase'].forEach(phaseType => {
      Object.keys(marketPhaseMap[phaseType]).forEach(phase => {
        const r = marketPhaseMap[phaseType][phase];
        const count = marketPhaseCountMap[phaseType][phase];
        
        marketPhasePerformance.push({
          phaseType: phaseType === 'marketSeason' ? 'עונת שוק' :
                    phaseType === 'weeklyWavePhase' ? 'פאזת וייקוף שבועי' :
                    'פאזת וייקוף יומי',
          phase,
          r,
          count,
          avgR: r / count
        });
      });
    });

    return {
        cumulativeR: computedCumulativeR,
        rByDay,
        winRateByDay,
        rBySymbol,
        rBySession,
        rByTrigger,
        rByReport,
        rByTimeDistribution,
        durationVsR,
        monthlyPerformance,
        successRateByTimeframe,
        tpDistribution,
        stopReasons,
        marketPhasePerformance,
        rByUser,  // New chart for admin mode
        stopsByDay  // New chart for stop losses by day
    };
  }, [realTrades]);

  // Available symbols for filtering
  const availableSymbols = useMemo(() => {
    const symbols = new Set();
    trades.forEach(trade => {
      if (trade.symbol) {
        symbols.add(trade.symbol);
      }
    });
    return ['all', ...Array.from(symbols)];
  }, [trades]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Handle navigation back to dashboard
  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };
  const handleBackToSimulator = () => {
    navigate('/tp-milestone-simulator');
  };
  // Handle user selection change in admin mode
  const handleUserSelectionChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedUsers(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  // Toggle admin panel expanded state
  const toggleAdminPanel = () => {
    setAdminPanelExpanded(!adminPanelExpanded);
  };

  // Handle compare mode toggle
  const handleCompareMode = (event) => {
    setIsCompareMode(event.target.checked);
  };

  // Custom Tooltip Component for Recharts
  const CustomTooltip = ({ active, payload, label, valuePrefix, valueSuffix }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1, bgcolor: 'rgba(255, 255, 255, 0.95)', boxShadow: theme.shadows[3] }}>
          <Typography variant="subtitle2">{label}</Typography>
          {payload.map((entry, index) => (
            <Box key={`tooltip-${index}`} sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: entry.color,
                  mr: 1
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {entry.name}: {valuePrefix || ''}{typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}{valueSuffix || ''}
              </Typography>
            </Box>
          ))}
        </Paper>
      );
    }
    return null;
  };

  // User comparison tooltip for admin mode
  const UserComparisonTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1, bgcolor: 'rgba(255, 255, 255, 0.95)', boxShadow: theme.shadows[3] }}>
          <Typography variant="subtitle2" fontWeight="bold">{label}</Typography>
          {payload.map((entry, index) => (
            <Box key={`tooltip-${index}`} sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: entry.color,
                  mr: 1
                }}
              />
              <Typography variant="body2">
                {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}
                {entry.dataKey === 'winRate' ? '%' : ''}
              </Typography>
            </Box>
          ))}
        </Paper>
      );
    }
    return null;
  };

  // Styles
  const styles = {
    root: {
      backgroundColor: alpha(theme.palette.primary.main, 0.05),
      minHeight: '100vh',
    },
    appBar: {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    },
    mainContent: {
      p: 3,
      direction: 'rtl',
      minHeight: 'calc(100vh - 64px)',
    },
    statsCard: {
      borderRadius: 16,
      boxShadow: '0 8px 16px rgba(0,0,0,0.05)',
      transition: 'transform 0.3s, box-shadow 0.3s',
      height: '100%',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
      },
    },
    chartCard: {
      borderRadius: 16,
      boxShadow: '0 8px 16px rgba(0,0,0,0.05)',
      transition: 'transform 0.3s, box-shadow 0.3s',
      height: '100%',
      overflow: 'hidden',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
      },
    },
    chartCardHeader: {
      p: 2,
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      bgcolor: alpha(theme.palette.primary.main, 0.05),
    },
    chartCardContent: {
      p: 2,
      pt: 1,
      height: 'calc(100% - 60px)',
    },
    sectionTitle: {
      mb: 2,
      display: 'flex',
      alignItems: 'center',
      '& .MuiSvgIcon-root': {
        mr: 1,
        color: theme.palette.primary.main,
      },
    },
    tabs: {
      bgcolor: alpha(theme.palette.background.paper, 0.6),
      borderRadius: 3,
      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
      mb: 3,
      '& .MuiTabs-indicator': {
        height: 4,
        borderRadius: 2,
      },
      '& .MuiTab-root': {
        minHeight: 60,
        fontWeight: 'bold',
        '&.Mui-selected': {
          color: theme.palette.primary.main,
        },
      },
    },
    filterBox: {
      display: 'flex',
      gap: 2,
      mb: 3,
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    actionButton: {
      borderRadius: 8,
      boxShadow: 'none',
      fontWeight: 500,
      textTransform: 'none',
    },
    positive: {
      color: theme.palette.success.main,
    },
    negative: {
      color: theme.palette.error.main,
    },
    tableContainer: {
      borderRadius: 16,
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
    },
    tableHeader: {
      backgroundColor: alpha(theme.palette.primary.main, 0.05),
    },
    tableHeaderCell: {
      fontWeight: 'bold',
      color: theme.palette.primary.main,
    },
    adminPanel: {
      p: 2,
      mb: 3,
      borderRadius: 2,
      bgcolor: alpha(theme.palette.secondary.main, 0.07),
      border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
    },
    userFilterSelect: {
      m: 1,
      minWidth: 200,
    },
    userCard: {
      p: 2,
      borderRadius: 2,
      bgcolor: alpha(theme.palette.primary.main, 0.05),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
    },
    userStatsBox: {
      display: 'flex', 
      alignItems: 'center', 
      p: 2,
      borderRadius: 2,
      bgcolor: theme.palette.background.paper,
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      },
    },
    userStatsLabel: {
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
    },
    userStatsValue: {
      fontWeight: 'bold',
      fontSize: '1.25rem',
    },
  };

  // דיאלוג לתמונות
  const ImageDialog = () => (
    <Dialog 
      open={imageDialogOpen} 
      onClose={() => setImageDialogOpen(false)}
      maxWidth="xl"
    >
      <DialogContent sx={{ p: 0 }}>
        <img 
          src={imageDialogUrl} 
          alt="תמונת העסקה" 
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setImageDialogOpen(false)}>סגור</Button>
      </DialogActions>
    </Dialog>
  );

  // דיאלוג פרטי עסקה
  const TradeDetailDialog = () => (
    <Dialog
      open={detailDialogOpen}
      onClose={() => setDetailDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      {detailTrade && (
        <>
          <DialogTitle>
            פרטי עסקה: {detailTrade.symbol} {detailTrade.outcome === 'Take Profit' ? 
              <Chip label="Take Profit" color="success" size="small" /> : 
              <Chip label="Stop Loss" color="error" size="small" />}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" color="primary" gutterBottom>מידע בסיסי</Typography>
                <Box sx={{ mr: 2 }}>
                  <Typography variant="body2"><b>תאריך:</b> {detailTrade.date}</Typography>
                  <Typography variant="body2"><b>יום:</b> {detailTrade.dayOfWeek}</Typography>
                  <Typography variant="body2"><b>שעה:</b> {detailTrade.time}</Typography>
                  <Typography variant="body2"><b>סשן:</b> {detailTrade.session}</Typography>
                  <Typography variant="body2"><b>טריגר תואם:</b> {detailTrade.matchedTrigger}</Typography>
                  <Typography variant="body2"><b>משך:</b> {formatDuration(detailTrade.duration)}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" color="primary" gutterBottom>ביצועים</Typography>
                <Box sx={{ mr: 2 }}>
                  <Typography variant="body2"><b>אחוז סטופ:</b> {detailTrade.stopPercentage}</Typography>
                  <Typography variant="body2"><b>נקודת יציאה:</b> {detailTrade.tpMilestoneExit}</Typography>
                  {detailTrade.outcome === 'Stop Loss' && (
                    <Typography variant="body2"><b>סיבת סטופ:</b> {detailTrade.stopReason}</Typography>
                  )}
                  <Typography variant="body2"><b>דוח:</b> {detailTrade.report || 'לא צוין'}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" color="primary" gutterBottom>אבני דרך TP</Typography>
                {detailTrade.tpMilestones && detailTrade.tpMilestones.length > 0 ? (
                  <TableContainer component={Paper} sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>TP</TableCell>
                          <TableCell>סטטוס</TableCell>
                          <TableCell>הגיע?</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detailTrade.tpMilestones.map((milestone, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{milestone.tp}</TableCell>
                            <TableCell>{milestone.status}</TableCell>
                            <TableCell>
                              {milestone.reached ? 
                                <CheckCircleIcon color="success" fontSize="small" /> : 
                                <WarningIcon color="error" fontSize="small" />}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">אין אבני דרך TP זמינות</Typography>
                )}
              </Grid>
              {(detailTrade.linkBefore || detailTrade.linkAfter) && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" color="primary" gutterBottom>צילומי מסך</Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {detailTrade.linkBefore && (
                      <Box>
                        <Typography variant="body2" gutterBottom>לפני:</Typography>
                        <img 
                          src={detailTrade.linkBefore} 
                          alt="לפני" 
                          style={{ maxWidth: '300px', maxHeight: '200px', cursor: 'pointer' }}
                          onClick={() => openImageDialog(detailTrade.linkBefore)}
                        />
                      </Box>
                    )}
                    {detailTrade.linkAfter && (
                      <Box>
                        <Typography variant="body2" gutterBottom>אחרי:</Typography>
                        <img 
                          src={detailTrade.linkAfter} 
                          alt="אחרי" 
                          style={{ maxWidth: '300px', maxHeight: '200px', cursor: 'pointer' }}
                          onClick={() => openImageDialog(detailTrade.linkAfter)}
                        />
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailDialogOpen(false)}>סגור</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  // דיאלוג להצגת עסקאות לפי תוצאה (Win/Loss)
  const OutcomeDialog = () => (
    <Dialog
      open={outcomeDialogOpen}
      onClose={() => setOutcomeDialogOpen(false)}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle>
        {selectedOutcome === 'Take Profit' ? 
          <Typography variant="h6" color="success.main">עסקאות רווחיות (Take Profit)</Typography> : 
          <Typography variant="h6" color="error.main">עסקאות הפסד (Stop Loss)</Typography>}
      </DialogTitle>
      <DialogContent>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>תאריך</TableCell>
                <TableCell>יום</TableCell>
                <TableCell>שעה</TableCell>
                <TableCell>סימבול</TableCell>
                <TableCell>סשן</TableCell>
                <TableCell>טריגר</TableCell>
                <TableCell>משך</TableCell>
                {selectedOutcome === 'Take Profit' ? 
                  <TableCell>נקודת יציאה</TableCell> : 
                  <TableCell>סיבת סטופ</TableCell>}
                <TableCell>פעולות</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {realTrades
                .filter(trade => trade.outcome === selectedOutcome)
                .map(trade => (
                  <TableRow key={trade.id}>
                    <TableCell>{trade.date}</TableCell>
                    <TableCell>{trade.dayOfWeek}</TableCell>
                    <TableCell>{trade.time}</TableCell>
                    <TableCell>{trade.symbol}</TableCell>
                    <TableCell>{trade.session}</TableCell>
                    <TableCell>{trade.matchedTrigger}</TableCell>
                    <TableCell>{formatDuration(trade.duration)}</TableCell>
                    {selectedOutcome === 'Take Profit' ? 
                      <TableCell>{trade.tpMilestoneExit || '-'}</TableCell> : 
                      <TableCell>{trade.stopReason || '-'}</TableCell>}
                    <TableCell>
                      <IconButton onClick={() => handleDetailClick(trade)}>
                        <InfoIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOutcomeDialogOpen(false)}>סגור</Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          bgcolor: alpha(theme.palette.primary.main, 0.05),
        }}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" sx={{ mt: 3, fontWeight: 'bold' }}>
          טוען נתונים...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          מאחזר את היסטוריית העסקאות
        </Typography>
      </Box>
    );
  }

  if (filteredTrades.length === 0) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          bgcolor: alpha(theme.palette.primary.main, 0.05),
        }}
      >
        <Box sx={{ position: 'relative', mb: 4 }}>
          <ShowChartIcon sx={{ fontSize: 80, color: alpha(theme.palette.text.secondary, 0.2) }} />
          <WarningIcon sx={{ 
            fontSize: 30, 
            color: theme.palette.warning.main,
            position: 'absolute',
            bottom: -5,
            right: -5,
            bgcolor: 'white',
            borderRadius: '50%',
            padding: '3px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
          {trades.length === 0 ? 'לא נמצאו עסקאות' : 'אין עסקאות העונות על תנאי הסינון'}
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3, maxWidth: 500 }}>
          {trades.length === 0 
            ? 'לא נמצאו עסקאות עבור המשתמשים שנבחרו. אנא בחר משתמשים אחרים או הוסף עסקאות בדף הדאשבורד.' 
            : 'נסה לשנות את תנאי הסינון כדי להציג עסקאות.'}
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToDashboard}
          sx={styles.actionButton}
        >
          חזרה לדאשבורד
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={styles.root}>
      <CssBaseline />
      
      {/* דיאלוגים */}
      <ImageDialog />
      <TradeDetailDialog />
      <OutcomeDialog />
      
      {/* Header */}
      <AppBar position="fixed" color="inherit" sx={styles.appBar}>
        <Toolbar>
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            {/* Logo and Title */}
            <Box display="flex" alignItems="center">
              <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                <AnalyticsIcon />
              </Avatar>
              <Typography variant="h6" fontWeight="bold" noWrap>
                ניתוח ביצועי המסחר | מאיץ הרווחים
                {isAdmin && (
                  <Chip 
                    label="פאנל מנהל" 
                    color="secondary" 
                    size="small" 
                    icon={<AdminPanelSettingsIcon />}
                    sx={{ ml: 1, fontWeight: 'bold' }} 
                  />
                )}
              </Typography>
            </Box>
            
            {/* Buttons */}
            <Box display="flex" alignItems="center" gap={1}>
            <Button 
                variant="outlined" 
                color="red" 
                sx={styles.actionButton}
                onClick={handleBackToSimulator}
              >
                לסימולטור סיכוי-סיכון
              </Button>
              <Button 
                variant="outlined" 
                color="primary" 
                startIcon={<ArrowBackIcon />}
                sx={styles.actionButton}
                onClick={handleBackToDashboard}
              >
                חזרה לדאשבורד
              </Button>
              <Button 
                variant="outlined" 
                color="secondary" 
                startIcon={<ExitToAppIcon />}
                sx={styles.actionButton}
                onClick={handleLogout}
              >
                התנתק
              </Button>
              
              <Tooltip title={user?.email || 'משתמש'}>
                <Avatar
                  sx={{ 
                    ml: 1, 
                    bgcolor: theme.palette.secondary.main,
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: '0 0 0 2px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <AccountCircleIcon />
                </Avatar>
              </Tooltip>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box component="main" sx={{ ...styles.mainContent, mt: 8 }}>
        {/* Page Title */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
              דוח סטטיסטי מתקדם
              {isAdmin && selectedUsers.length === 1 && (
                <Typography variant="h6" component="span" color="text.secondary" sx={{ mr: 1 }}>
                  ({allUsers.find(u => u.id === selectedUsers[0])?.userName || 'משתמש'})
                </Typography>
              )}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              ניתוח מעמיק של {realTrades.length} עסקאות {isAdmin && selectedUsers.length > 1 ? `עבור ${selectedUsers.length} משתמשים` : ''}
            </Typography>
          </Box>
        </Box>

        {/* Admin Panel */}
        {isAdmin && (
          <Paper sx={styles.adminPanel}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: adminPanelExpanded ? 2 : 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SupervisorAccountIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="secondary" fontWeight="bold">
                  פאנל ניהול
                </Typography>
              </Box>
              <Box>
                <IconButton onClick={toggleAdminPanel}>
                  {adminPanelExpanded ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                </IconButton>
              </Box>
            </Box>
            
            {adminPanelExpanded && (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
                  <FormControl sx={styles.userFilterSelect}>
                    <InputLabel id="selected-users-label">בחר משתמשים</InputLabel>
                    <Select
                      labelId="selected-users-label"
                      id="selected-users"
                      multiple
                      value={selectedUsers}
                      onChange={handleUserSelectionChange}
                      input={<OutlinedInput label="בחר משתמשים" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((userId) => {
                            const user = allUsers.find(u => u.id === userId);
                            return (
                              <Chip 
                                key={userId} 
                                label={user?.userName || userId} 
                                size="small"
                                avatar={<Avatar><PersonIcon /></Avatar>}
                              />
                            );
                          })}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {allUsers.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          <Checkbox checked={selectedUsers.indexOf(user.id) > -1} />
                          <ListItemText 
                            primary={user.userName} 
                            secondary={`${user.email} | ${user.trigger}`} 
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControl sx={{ minWidth: 150 }}>
                    <InputLabel id="trigger-filter-label">סנן לפי טריגר</InputLabel>
                    <Select
                      labelId="trigger-filter-label"
                      id="trigger-filter"
                      value={userTriggerFilter}
                      onChange={(e) => setUserTriggerFilter(e.target.value)}
                      label="סנן לפי טריגר"
                    >
                      {availableTriggers.map((trigger) => (
                        <MenuItem key={trigger} value={trigger}>
                          {trigger === 'all' ? 'כל הטריגרים' : trigger}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={isCompareMode} 
                        onChange={handleCompareMode} 
                        color="secondary"
                      />
                    }
                    label="השוואת משתמשים"
                  />
                  
                  <Button 
                    variant="contained" 
                    color="secondary" 
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                      setLoading(true);
                      setTimeout(() => setLoading(false), 500);
                    }}
                  >
                    רענן נתונים
                  </Button>
                </Box>
                
                {isCompareMode && selectedUsers.length > 1 && (
                  <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                    <Typography variant="h6" color="primary" gutterBottom>
                      <CompareIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      השוואת ביצועים
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <ResponsiveContainer width="100%" height={350}>
                          <BarChart
                            data={userStats}
                            margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="userName" />
                            <YAxis yAxisId="left" orientation="left" label={{ value: 'Average R', angle: -90, position: 'insideLeft' }} />
                            <YAxis yAxisId="right" orientation="right" domain={[0, 100]} label={{ value: 'Win Rate (%)', angle: 90, position: 'insideRight' }} />
                            <RechartsTooltip content={<UserComparisonTooltip />} />
                            <Legend />
                            <Bar yAxisId="left" dataKey="avgR" name="Average R" fill={theme.palette.primary.main}>
                              {userStats.map((entry, index) => (
                                <Cell key={`cell-avgR-${index}`} fill={entry.avgR > 0 ? theme.palette.success.main : theme.palette.error.main} />
                              ))}
                            </Bar>
                            <Line yAxisId="right" type="monotone" dataKey="winRate" name="Win Rate %" stroke={theme.palette.secondary.main} strokeWidth={2} />
                          </BarChart>
                        </ResponsiveContainer>
                      </Grid>
                    </Grid>
                  </Paper>
                )}
                
                {selectedUsers.length > 0 && !isCompareMode && (
                  <Grid container spacing={2}>
                    {userStats.map(user => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={user.userId}>
                        <Box sx={styles.userCard}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 1 }}>
                                <PersonIcon />
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle1" fontWeight="bold">{user.userName}</Typography>
                                <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                              </Box>
                            </Box>
                            <Chip 
                              label={user.trigger} 
                              size="small" 
                              color="secondary"
                            />
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary">
                            שעות מסחר: {user.tradingHours}
                          </Typography>
                          
                          <Divider sx={{ my: 1 }} />
                          
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Box sx={styles.userStatsBox}>
                                <Box sx={{ textAlign: 'center', width: '100%' }}>
                                  <Typography sx={styles.userStatsLabel}>סה"כ R</Typography>
                                  <Typography 
                                    sx={{
                                      ...styles.userStatsValue,
                                      color: user.totalR > 0 ? theme.palette.success.main : theme.palette.error.main
                                    }}
                                  >
                                    {user.totalR.toFixed(2)}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={styles.userStatsBox}>
                                <Box sx={{ textAlign: 'center', width: '100%' }}>
                                  <Typography sx={styles.userStatsLabel}>ממוצע</Typography>
                                  <Typography 
                                    sx={{
                                      ...styles.userStatsValue,
                                      color: user.avgR > 0 ? theme.palette.success.main : theme.palette.error.main
                                    }}
                                  >
                                    {user.avgR.toFixed(2)} R
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={styles.userStatsBox}>
                                <Box sx={{ textAlign: 'center', width: '100%' }}>
                                  <Typography sx={styles.userStatsLabel}>אחוז הצלחה</Typography>
                                  <Typography 
                                    sx={{
                                      ...styles.userStatsValue,
                                      color: user.winRate > 50 ? theme.palette.success.main : 
                                            user.winRate > 40 ? theme.palette.warning.main : 
                                            theme.palette.error.main
                                    }}
                                  >
                                    {user.winRate.toFixed(1)}%
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={styles.userStatsBox}>
                                <Box sx={{ textAlign: 'center', width: '100%' }}>
                                  <Typography sx={styles.userStatsLabel}>עסקאות</Typography>
                                  <Typography sx={styles.userStatsValue}>
                                    {user.totalTrades}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            )}
          </Paper>
        )}

        {/* Filter Box */}
        <Box sx={styles.filterBox}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FilterAltIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="body2" sx={{ ml: 1 }}>סננו לפי:</Typography>
          </Box>
          
          <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
            <InputLabel>תקופה</InputLabel>
            <Select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
              label="תקופה"
            >
              <MenuItem value="all">כל הזמנים</MenuItem>
              <MenuItem value="last30">30 ימים אחרונים</MenuItem>
              <MenuItem value="last90">90 ימים אחרונים</MenuItem>
              <MenuItem value="thisYear">שנה נוכחית</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
            <InputLabel>סימבול</InputLabel>
            <Select
              value={symbolFilter}
              onChange={(e) => setSymbolFilter(e.target.value)}
              label="סימבול"
            >
              {availableSymbols.map((symbol) => (
                <MenuItem key={symbol} value={symbol}>
                  {symbol === 'all' ? 'כל הסימבולים' : symbol}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {isAdmin && (
            <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
              <InputLabel>TP Milestone</InputLabel>
              <Select
                value={tpMilestoneFilter}
                onChange={(e) => setTpMilestoneFilter(e.target.value)}
                label="TP Milestone"
              >
                {availableTpMilestones.map((milestone) => (
                  <MenuItem key={milestone} value={milestone}>
                    {milestone === 'all' ? 'כל ה-TP' : milestone}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {/* הודעה אם אין עסקאות עם טריגר */}
        {filteredTrades.length > 0 && !filteredTrades.some(t => t.triggerAppeared === 'yes') && (
          <Box 
            sx={{ 
              mb: 3, 
              p: 2, 
              borderRadius: 2, 
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <WarningIcon color="warning" sx={{ mr: 2 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" color="warning.main">
                שים לב: אין עסקאות עם טריגר
              </Typography>
              <Typography variant="body2">
                כל העסקאות שלך הן ללא טריגר (triggerAppeared = 'no'). חלק מהמדדים הסטטיסטיים ייתכן ויציגו ערכים שאינם מדויקים.
              </Typography>
            </Box>
          </Box>
        )}

        {/* Summary Cards Row */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ ...styles.statsCard, borderTop: `4px solid ${theme.palette.primary.main}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    סה"כ R
                  </Typography>
                  <Tooltip title="סכום כל ערכי ה-R מכל העסקאות" arrow placement="top">
                    <IconButton size="small">
                      <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography 
                  variant="h3" 
                  fontWeight="bold"
                  color={stats.totalR > 0 ? 'success.main' : 'error.main'}
                  sx={{ mb: 1 }}
                >
                  {stats.totalR.toFixed(2)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography 
                    variant="body2" 
                    color="text.secondary"
                  >
                    ממוצע:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    fontWeight="medium"
                    sx={{ ml: 1, color: stats.avgR > 0 ? theme.palette.success.main : theme.palette.error.main }}
                  >
                    {stats.avgR.toFixed(2)} R לעסקה
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ ...styles.statsCard, borderTop: `4px solid ${theme.palette.success.main}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    אחוז הצלחה
                  </Typography>
                  <Tooltip title="אחוז העסקאות שהסתיימו ברווח" arrow placement="top">
                    <IconButton size="small">
                      <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography 
                  variant="h3" 
                  fontWeight="bold"
                  color={stats.winRate > 50 ? 'success.main' : 'error.main'}
                  sx={{ mb: 1 }}
                >
                  {stats.winRate.toFixed(1)}%
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                  >
                    יחס:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    fontWeight="medium"
                    sx={{ ml: 1 }}
                  >
                    {stats.winCount} מתוך {stats.totalTrades} עסקאות
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ ...styles.statsCard, borderTop: `4px solid ${theme.palette.warning.main}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    פקטור רווח
                  </Typography>
                  <Tooltip title="יחס בין רווח כולל להפסד כולל" arrow placement="top">
                    <IconButton size="small">
                      <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography 
                  variant="h3" 
                  fontWeight="bold"
                  color={stats.profitFactor > 1 ? 'success.main' : 'error.main'}
                  sx={{ mb: 1 }}
                >
                  {stats.profitFactor.toFixed(2)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                  >
                    תוחלת:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    fontWeight="medium"
                    sx={{ 
                      ml: 1,
                      color: stats.expectancy > 0 ? theme.palette.success.main : theme.palette.error.main
                    }}
                  >
                    {stats.expectancy.toFixed(2)} R
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ ...styles.statsCard, borderTop: `4px solid ${theme.palette.info.main}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    הטוב והגרוע
                  </Typography>
                  <Tooltip title="ערכי R המקסימליים והמינימליים" arrow placement="top">
                    <IconButton size="small">
                      <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box>
                    <Typography variant="body2" color="success.main" fontWeight="medium">
                      עסקה הטובה ביותר
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                      +{stats.bestR.toFixed(1)}R
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" color="error.main" fontWeight="medium">
                      עסקה הגרועה ביותר
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="error.main">
                      {stats.worstR.toFixed(1)}R
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                  >
                    משך ממוצע:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    fontWeight="medium"
                    sx={{ ml: 1 }}
                  >
                    {formatDuration(stats.averageDuration)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs for Different Chart Sections */}
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          variant="scrollable"
          scrollButtons="auto"
          sx={styles.tabs}
        >
          <Tab icon={<TimelineIcon />} label="ביצועים לאורך זמן" />
          <Tab icon={<BarChartIcon />} label="פילוח לפי קטגוריות" />
          <Tab icon={<PieChartIcon />} label="התפלגויות" />
          <Tab icon={<InsightsIcon />} label="ניתוח מתקדם" />
          <Tab icon={<FormatListNumberedIcon />} label="טבלאות מסכמות" />
          <Tab icon={<SummarizeIcon />} label="תובנות וטיפים" />
          {isAdmin && (
            <Tab icon={<SupervisorAccountIcon />} label="ניתוח מנהל" />
          )}
        </Tabs>

        {/* Tab Content */}
        {/* Tab 1: Performance Over Time */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            {/* Cumulative R Chart */}
            <Grid item xs={12}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <TimelineIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    התקדמות מצטברת (R) לאורך זמן
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={400}>
                    <ComposedChart
                      data={chartsData.cumulativeR}
                      margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis 
                        dataKey="tradeNumber" 
                        label={{ 
                          value: 'מספר עסקה', 
                          position: 'insideBottomRight', 
                          offset: -10,
                          style: { textAnchor: 'end' } 
                        }} 
                      />
                      <YAxis 
                        domain={['auto', 'auto']}
                        label={{ 
                          value: 'R מצטבר', 
                          angle: -90, 
                          position: 'insideLeft',
                          style: { textAnchor: 'middle' } 
                        }} 
                      />
                      <RechartsTooltip 
                        content={<CustomTooltip />} 
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="cumulative" 
                        name="R מצטבר"
                        fill={alpha(theme.palette.primary.main, 0.2)} 
                        stroke={theme.palette.primary.main} 
                        strokeWidth={2} 
                      />
                      <Bar 
                        dataKey="r" 
                        name="R לעסקה" 
                        barSize={20}
                        fill={theme.palette.success.main}
                      >
                        {chartsData.cumulativeR.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.r > 0 ? theme.palette.success.light : theme.palette.error.light} 
                          />
                        ))}
                      </Bar>
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            
            {/* R Correlation Analysis */}
            <Grid item xs={12} md={6}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <TroubleshootIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    מתאם בין משך עסקה לרווחיות
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={350}>
                    <ScatterChart
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis 
                        type="number" 
                        dataKey="duration" 
                        name="משך (דקות)" 
                        label={{ value: 'משך (דקות)', position: 'insideBottom', offset: -10 }} 
                        tickFormatter={(value) => formatDuration(value)}
                      />
                      <YAxis 
                        type="number" 
                        dataKey="r" 
                        name="ערך R" 
                        label={{ value: 'ערך R', angle: -90, position: 'insideLeft' }} 
                      />
                      <RechartsTooltip 
                        cursor={{ strokeDasharray: '3 3' }}
                        formatter={(value, name) => {
                          if (name === 'משך (דקות)') {
                            return [formatDuration(value), name];
                          }
                          return [value.toFixed(2), name];
                        }}
                      />
                      <Legend />
                      <Scatter 
                        name="עסקות בהפסד" 
                        data={chartsData.durationVsR.filter(d => d.outcome === 'Stop Loss')} 
                        fill={theme.palette.error.main} 
                        shape="circle" 
                      />
                      <Scatter 
                        name="עסקות ברווח" 
                        data={chartsData.durationVsR.filter(d => d.outcome === 'Take Profit')} 
                        fill={theme.palette.success.main} 
                        shape="circle" 
                      />
                    </ScatterChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            
            {/* Symbol Performance Over Time */}
            <Grid item xs={12} md={6}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <StackedBarChartIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    ביצועי סימבולים לאורך זמן
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={350}>
                    <LineChart
                      data={chartsData.cumulativeR}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis dataKey="tradeNumber" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      {Array.from(new Set(chartsData.cumulativeR.map(d => d.symbol))).map((symbol, index) => (
                        <Line 
                          key={symbol}
                          type="monotone" 
                          dataKey="r" 
                          name={symbol} 
                          stroke={COLORS[index % COLORS.length]} 
                          strokeWidth={2}
                          dot={{ r: 3 }}
                          connectNulls
                          data={chartsData.cumulativeR.filter(d => d.symbol === symbol)}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            
            {/* R Value Distribution */}
            <Grid item xs={12}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <AttachMoneyIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    התפלגות ערכי R (הצלחה מול כישלון)
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart
                      data={[
                        { name: '-1 R (הפסד)', value: stats.lossCount },
                        ...Array.from(
                          new Set(
                            stats.rValues
                              .filter(t => t.r > 0)
                              .map(t => Math.floor(t.r))
                          )
                        ).sort((a, b) => a - b).map(r => ({
                          name: `${r} R+`,
                          value: stats.rValues.filter(t => t.r >= r && t.r < r + 1).length
                        }))
                      ]}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis dataKey="name" />
                      <YAxis label={{ value: 'מספר עסקאות', angle: -90, position: 'insideLeft' }} />
                      <RechartsTooltip />
                      <Legend />
                      <Bar 
                        dataKey="value" 
                        name="מספר עסקאות" 
                        fill={theme.palette.primary.main}
                        barSize={60}
                      >
                        {[
                          { name: '-1 R (הפסד)', value: stats.lossCount },
                          ...Array.from(
                            new Set(
                              stats.rValues
                                .filter(t => t.r > 0)
                                .map(t => Math.floor(t.r))
                            )
                          ).sort((a, b) => a - b).map(r => ({
                            name: `${r} R+`,
                            value: stats.rValues.filter(t => t.r >= r && t.r < r + 1).length
                          }))
                        ].map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.name.includes('הפסד') ? theme.palette.error.main : theme.palette.success.main} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            
            {/* Monthly Performance Chart */}
            <Grid item xs={12} md={8}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <DateRangeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    ביצועים חודשיים
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={350}>
                    <ComposedChart
                      data={chartsData.monthlyPerformance}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis dataKey="month" />
                      <YAxis 
                        yAxisId="left" 
                        orientation="left" 
                        label={{ value: 'סה"כ R', angle: -90, position: 'insideLeft' }} 
                      />
                      <YAxis 
                        yAxisId="right" 
                        orientation="right" 
                        label={{ value: 'מספר עסקאות', angle: 90, position: 'insideRight' }} 
                      />
                      <RechartsTooltip 
                        content={<CustomTooltip />} 
                      />
                      <Legend />
                      <Bar 
                        yAxisId="left" 
                        dataKey="r" 
                        name="סך הכל R"
                        barSize={30}
                      >
                        {chartsData.monthlyPerformance.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.r > 0 ? theme.palette.success.main : theme.palette.error.main} 
                          />
                        ))}
                      </Bar>
                      <Line 
                        yAxisId="right" 
                        type="monotone" 
                        dataKey="count" 
                        name="מספר עסקאות" 
                        stroke={theme.palette.info.main} 
                        strokeWidth={2}
                        dot={{ r: 5 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            
            {/* Win Rate by Month */}
            <Grid item xs={12} md={4}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <TrendingUpIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    אחוז הצלחה לפי חודש
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={350}>
                    <LineChart
                      data={chartsData.monthlyPerformance}
                      margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis dataKey="month" />
                      <YAxis domain={[0, 100]} label={{ value: 'אחוז הצלחה (%)', angle: -90, position: 'insideLeft' }} />
                      <RechartsTooltip 
                        content={<CustomTooltip valueSuffix="%" />} 
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="winRate" 
                        name="אחוז הצלחה" 
                        stroke={theme.palette.success.main} 
                        strokeWidth={2}
                        dot={{ r: 6, fill: theme.palette.success.main }}
                        activeDot={{ r: 8 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="avgR"
                        name="ממוצע R" 
                        stroke={theme.palette.info.main} 
                        strokeWidth={2}
                        dot={{ r: 6, fill: theme.palette.info.main }}
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            
            {/* Daily Performance */}
            <Grid item xs={12} md={6}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <CalendarTodayIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    ביצועים לפי יום בשבוע
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart
                      data={chartsData.rByDay}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis dataKey="day" />
                      <YAxis label={{ value: 'סה"כ R', angle: -90, position: 'insideLeft' }} />
                      <RechartsTooltip 
                        content={<CustomTooltip />} 
                      />
                      <Legend />
                      <Bar 
                        dataKey="r" 
                        name="סך הכל R"
                        fill={theme.palette.primary.main}
                        barSize={40}
                      >
                        {chartsData.rByDay.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.r > 0 ? theme.palette.success.main : theme.palette.error.main} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            
            {/* Win Rate by Day */}
            <Grid item xs={12} md={6}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <EqualizerIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    אחוז הצלחה לפי יום בשבוע
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={350}>
                    <ComposedChart
                      data={chartsData.winRateByDay}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis dataKey="day" />
                      <YAxis yAxisId="left" orientation="left" domain={[0, 100]} label={{ value: 'אחוז הצלחה (%)', angle: -90, position: 'insideLeft' }} />
                      <YAxis yAxisId="right" orientation="right" label={{ value: 'מספר עסקאות', angle: 90, position: 'insideRight' }} />
                      <RechartsTooltip 
                        content={<CustomTooltip />} 
                      />
                      <Legend />
                      <Bar 
                        yAxisId="right" 
                        dataKey="totalTrades" 
                        name="מספר עסקאות" 
                        fill={alpha(theme.palette.info.main, 0.7)}
                        barSize={20}
                      />
                      <Line 
                        yAxisId="left" 
                        type="monotone" 
                        dataKey="winRate" 
                        name="אחוז הצלחה" 
                        stroke={theme.palette.success.main} 
                        strokeWidth={3}
                        dot={{ r: 6, fill: theme.palette.success.main }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            
            {/* Time Distribution */}
            <Grid item xs={12}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <AccessTimeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    התפלגות ביצועים לפי שעות היום
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={350}>
                    <ComposedChart
                      data={chartsData.rByTimeDistribution}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis 
                        dataKey="time" 
                        interval={0} 
                        angle={-45} 
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis yAxisId="left" orientation="left" label={{ value: 'סה"כ R', angle: -90, position: 'insideLeft' }} />
                      <YAxis yAxisId="right" orientation="right" domain={[0, 'auto']} label={{ value: 'מספר עסקאות', angle: 90, position: 'insideRight' }} />
                      <RechartsTooltip 
                        content={<CustomTooltip />} 
                      />
                      <Legend />
                      <Bar 
                        yAxisId="left" 
                        dataKey="r" 
                        name="סך הכל R"
                        fill={theme.palette.primary.main}
                        barSize={30}
                      >
                        {chartsData.rByTimeDistribution.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.r > 0 ? theme.palette.success.main : theme.palette.error.main} 
                          />
                        ))}
                      </Bar>
                      <Line 
                        yAxisId="right" 
                        type="monotone" 
                        dataKey="count" 
                        name="מספר עסקאות" 
                        stroke={theme.palette.info.main}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                      />
                      <Line 
                        yAxisId="left" 
                        type="monotone" 
                        dataKey="avgR" 
                        name="ממוצע R" 
                        stroke={theme.palette.warning.main}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Tab 2: Breakdown by Categories */}
        {activeTab === 1 && (
          <Grid container spacing={3}>
            {/* Symbol Performance */}
            <Grid item xs={12} md={6}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <CurrencyBitcoinIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    ביצועים לפי סימבול
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart
                      data={chartsData.rBySymbol}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis type="number" />
                      <YAxis dataKey="symbol" type="category" width={70} />
                      <RechartsTooltip 
                        content={<CustomTooltip />} 
                      />
                      <Legend />
                      <Bar 
                        dataKey="r" 
                        name="סך הכל R"
                        fill={theme.palette.primary.main}
                        barSize={20}
                      >
                        {chartsData.rBySymbol.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.r > 0 ? theme.palette.success.main : theme.palette.error.main} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            
            {/* Session Performance */}
            <Grid item xs={12} md={6}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <WaterfallChartIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    ביצועים לפי סשן
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart
                      data={chartsData.rBySession}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis type="number" />
                      <YAxis dataKey="session" type="category" width={70} />
                      <RechartsTooltip 
                        content={<CustomTooltip />} 
                      />
                      <Legend />
                      <Bar 
                        dataKey="r" 
                        name="סך הכל R"
                        fill={theme.palette.primary.main}
                        barSize={20}
                      >
                        {chartsData.rBySession.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.r > 0 ? theme.palette.success.main : theme.palette.error.main} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            
            {/* Trigger Performance */}
            <Grid item xs={12}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <SignalCellularAltIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    ביצועים לפי טריגר
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={350}>
                    <ComposedChart
                      data={chartsData.rByTrigger}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis dataKey="trigger" />
                      <YAxis yAxisId="left" orientation="left" label={{ value: 'סה"כ R', angle: -90, position: 'insideLeft' }} />
                      <YAxis yAxisId="right" orientation="right" domain={[0, 'auto']} label={{ value: 'מספר עסקאות', angle: 90, position: 'insideRight' }} />
                      <RechartsTooltip 
                        content={<CustomTooltip />} 
                      />
                      <Legend />
                      <Bar 
                        yAxisId="left" 
                        dataKey="r" 
                        name="סך הכל R"
                        fill={theme.palette.primary.main}
                        barSize={40}
                      >
                        {chartsData.rByTrigger.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.r > 0 ? theme.palette.success.main : theme.palette.error.main} 
                          />
                        ))}
                      </Bar>
                      <Line 
                        yAxisId="right" 
                        type="monotone" 
                        dataKey="count" 
                        name="מספר עסקאות" 
                        stroke={theme.palette.info.main}
                        strokeWidth={2}
                        dot={{ r: 5 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            
            {/* Report Performance */}
            <Grid item xs={12} md={6}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <PresentToAllIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    ביצועים לפי דוח
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={chartsData.rByReport}
                      margin={{ top: 20, right: 30, left: 20, bottom: 120 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis 
                        dataKey="report" 
                        interval={0}
                        angle={-45}
                        textAnchor="end"
                        height={100}
                      />
                      <YAxis label={{ value: 'ממוצע R', angle: -90, position: 'insideLeft' }} />
                      <RechartsTooltip 
                        content={<CustomTooltip />} 
                      />
                      <Legend />
                      <Bar 
                        dataKey="avgR" 
                        name="ממוצע R" 
                        fill={theme.palette.primary.main}
                        barSize={40}
                      >
                        {chartsData.rByReport.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.avgR > 0 ? theme.palette.success.main : theme.palette.error.main} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            
            {/* Market Phase Performance */}
            <Grid item xs={12} md={6}>
  <Paper sx={styles.chartCard}>
    <Box sx={styles.chartCardHeader}>
      <Typography variant="subtitle1" fontWeight="bold" color="primary">
        <DonutLargeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
        ביצועים לפי פאזות שוק
      </Typography>
    </Box>
    <Box sx={styles.chartCardContent}>
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart
          data={chartsData.marketPhasePerformance}
          margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
          <XAxis 
            dataKey="phase" 
            angle={-45} 
            textAnchor="end"
            height={80}
            interval={0}
          />
          <YAxis 
            yAxisId="left" 
            label={{ value: 'ממוצע R', angle: -90, position: 'insideLeft' }} 
          />
          <YAxis 
            yAxisId="right" 
            orientation="right" 
            label={{ value: 'מספר עסקאות', angle: 90, position: 'insideRight' }} 
          />
          <RechartsTooltip 
            content={({ active, payload, label }) => {
              if (active && payload && payload.length) {
                return (
                  <Paper sx={{ p: 2, direction: 'rtl' }}>
                    <Typography variant="subtitle2" fontWeight="bold">{label}</Typography>
                    {payload.map((entry, index) => (
                      <Typography key={index} variant="body2">
                        {entry.name}: {entry.value.toFixed(2)}
                        {entry.dataKey === 'count' ? ' עסקאות' : ' R'}
                      </Typography>
                    ))}
                  </Paper>
                );
              }
              return null;
            }} 
          />
          <Legend />
          <Bar 
            yAxisId="left"
            dataKey="avgR" 
            name="ממוצע R" 
            barSize={40}
          >
            {chartsData.marketPhasePerformance.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={
                  entry.phase.includes('Phase A') ? theme.palette.error.main :
                  entry.phase.includes('Phase B') ? theme.palette.warning.light :
                  entry.phase.includes('Phase C') ? theme.palette.info.main :
                  entry.phase.includes('Phase D') ? theme.palette.success.light :
                  entry.phase.includes('Phase E') ? theme.palette.primary.main :
                  theme.palette.grey[500]
                } 
              />
            ))}
          </Bar>
          <Line 
            yAxisId="right"
            type="monotone"
            dataKey="count"
            name="מספר עסקאות"
            stroke={theme.palette.primary.main}
            strokeWidth={2}
            dot={{ r: 5 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  </Paper>
</Grid>            
            {/* Timeframe Success Rates */}
            <Grid item xs={12}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <CandlestickChartIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    אחוזי הצלחה לפי מסגרות זמן
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={400}>
                    <ComposedChart
                      data={chartsData.successRateByTimeframe}
                      margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis 
                        dataKey="timeframe" 
                        interval={0}
                        tickFormatter={(value) => value.length > 10 ? `${value.substring(0, 8)}...` : value}
                      />
                      <YAxis yAxisId="left" orientation="left" domain={[0, 100]} label={{ value: 'אחוז הצלחה (%)', angle: -90, position: 'insideLeft' }} />
                      <YAxis yAxisId="right" orientation="right" domain={[0, 'auto']} label={{ value: 'מספר עסקאות', angle: 90, position: 'insideRight' }} />
                      <RechartsTooltip 
                        content={<CustomTooltip />} 
                      />
                      <Legend />
                      <Bar 
                        yAxisId="right" 
                        dataKey="count" 
                        name="מספר עסקאות" 
                        fill={alpha(theme.palette.info.main, 0.7)}
                        barSize={20}
                      />
                      <Line 
                        yAxisId="left" 
                        type="monotone" 
                        dataKey="successRate" 
                        name="אחוז הצלחה" 
                        stroke={theme.palette.success.main}
                        strokeWidth={3}
                        dot={{ r: 5, fill: theme.palette.success.main }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            {/* User Performance (Admin Only) */}
            {isAdmin && selectedUsers.length > 1 && (
              <Grid item xs={12}>
                <Paper sx={styles.chartCard}>
                  <Box sx={styles.chartCardHeader}>
                    <Typography variant="subtitle1" fontWeight="bold" color="secondary">
                      <SupervisorAccountIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                      ביצועים לפי משתמש
                    </Typography>
                  </Box>
                  <Box sx={styles.chartCardContent}>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart
                        data={chartsData.rByUser}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                        layout="vertical"
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                        <XAxis type="number" />
                        <YAxis dataKey="user" type="category" width={100} />
                        <RechartsTooltip 
                          content={<CustomTooltip />} 
                        />
                        <Legend />
                        <Bar 
                          dataKey="avgR" 
                          name="ממוצע R" 
                          fill={theme.palette.secondary.main}
                          barSize={20}
                        >
                          {chartsData.rByUser.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={entry.avgR > 0 ? theme.palette.success.main : theme.palette.error.main} 
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>
            )}
              
            {/* Stop Losses by Day */}
            <Grid item xs={12}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <TrendingDownIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    סטופים לפי ימים
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={350}>
                    <ComposedChart
                      data={chartsData.stopsByDay}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis dataKey="day" />
                      <YAxis yAxisId="left" orientation="left" label={{ value: 'מספר סטופים', angle: -90, position: 'insideLeft' }} />
                      <YAxis yAxisId="right" orientation="right" domain={[0, 100]} label={{ value: 'אחוז מכלל העסקאות (%)', angle: 90, position: 'insideRight' }} />
                      <RechartsTooltip />
                      <Legend />
                      <Bar 
                        yAxisId="left" 
                        dataKey="stops" 
                        name="מספר סטופים" 
                        fill={theme.palette.error.main}
                        barSize={40}
                      />
                      <Line 
                        yAxisId="right" 
                        type="monotone" 
                        dataKey="stopsPercentage" 
                        name="אחוז סטופים" 
                        stroke={theme.palette.warning.main}
                        strokeWidth={2}
                        dot={{ r: 5 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Tab 3: Distributions */}
        {activeTab === 2 && (
          <Grid container spacing={3}>
            {/* Win/Loss Distribution */}
            <Grid item xs={12} md={6}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <PieChartIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    חלוקת רווח/הפסד
                  </Typography>
                </Box>
                <Box sx={{ ...styles.chartCardContent, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Take Profit', value: stats.winCount },
                          { name: 'Stop Loss', value: stats.lossCount }
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={100}
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                        onClick={(data) => handleOutcomeClick(data.name)}
                      >
                        <Cell fill={theme.palette.success.main} style={{ cursor: 'pointer' }} />
                        <Cell fill={theme.palette.error.main} style={{ cursor: 'pointer' }} />
                      </Pie>
                      <RechartsTooltip 
                        formatter={(value, name) => [
                          `${value} trades (click to view)`, 
                          name
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box sx={{ display: 'flex', justifyContent: 'space-around', width: '100%', mt: 2 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography 
                        variant="h6" 
                        color="success.main" 
                        fontWeight="bold"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleOutcomeClick('Take Profit')}
                      >
                        {stats.winCount}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        עסקאות ברווח
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography 
                        variant="h6" 
                        color="error.main" 
                        fontWeight="bold"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleOutcomeClick('Stop Loss')}
                      >
                        {stats.lossCount}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        עסקאות בהפסד
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" fontWeight="bold">
                        {stats.winRate.toFixed(1)}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        אחוז הצלחה
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            
            {/* TP Milestones Distribution */}
            <Grid item xs={12} md={6}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <EmojiEventsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    התפלגות טייק פרופיט
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart
                      data={chartsData.tpDistribution}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis dataKey="tp" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Bar 
                        dataKey="count" 
                        name="מספר עסקאות" 
                        fill={theme.palette.primary.main}
                        barSize={30}
                      >
                        {chartsData.tpDistribution.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={
                              entry.tp === "-1R" ? theme.palette.error.main : 
                              COLORS[index % COLORS.length]
                            } 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            
{/* Stop Loss Reasons Distribution */}
<Grid item xs={12} md={6}>
  <Paper sx={styles.chartCard}>
    <Box sx={styles.chartCardHeader}>
      <Typography variant="subtitle1" fontWeight="bold" color="primary">
        <TrendingDownIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
        סיבות לסטופ לוס
      </Typography>
    </Box>
    <Box sx={styles.chartCardContent}>
      <ResponsiveContainer width="100%" height={350}>
        <PieChart>
          <Pie
            data={chartsData.stopReasons}
            cx="50%"
            cy="50%"
            labelLine={true}
            outerRadius={130}
            fill="#8884d8"
            dataKey="count"
            nameKey="reason"
            label={({ reason, percent }) =>
              `${reason.length > 15 ? reason.substring(0, 15) + '...' : reason}: ${(percent * 100).toFixed(1)}%`
            }
            onClick={(data) => handleStopReasonClick(data.reason)}
          >
            {chartsData.stopReasons.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
                style={{ cursor: 'pointer' }}
              />
            ))}
          </Pie>
          <RechartsTooltip 
            formatter={(value, name) => [
              `${value} עסקאות (לחץ לפרטים)`, 
              name
            ]} 
          />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  </Paper>
</Grid>

            {/* Trade Duration Distribution */}
            <Grid item xs={12} md={6}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <HourglassEmptyIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    התפלגות משך עסקאות
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={350}>
                    <ScatterChart
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis 
                        type="number" 
                        dataKey="duration" 
                        name="משך (דקות)" 
                        label={{ value: 'משך (דקות)', position: 'insideBottomRight', offset: -10 }} 
                        tickFormatter={(value) => formatDuration(value)}
                      />
                      <YAxis 
                        type="number" 
                        dataKey="r" 
                        name="ערך R" 
                        label={{ value: 'ערך R', angle: -90, position: 'insideLeft' }} 
                      />
                      <ZAxis 
                        type="number" 
                        range={[50, 500]} 
                        dataKey="count" 
                      />
                      <RechartsTooltip 
                        cursor={{ strokeDasharray: '3 3' }}
                        formatter={(value, name) => {
                          if (name === 'משך (דקות)') {
                            return [formatDuration(value), name];
                          }
                          return [value, name];
                        }}
                      />
                      <Legend />
                      <Scatter 
                        name="עסקות בהפסד" 
                        data={chartsData.durationVsR.filter(d => d.outcome === 'Stop Loss')} 
                        fill={theme.palette.error.main} 
                        shape="circle" 
                      />
                      <Scatter 
                        name="עסקות ברווח" 
                        data={chartsData.durationVsR.filter(d => d.outcome === 'Take Profit')} 
                        fill={theme.palette.success.main} 
                        shape="circle" 
                      />
                    </ScatterChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            
            {/* Symbol and Session Distribution */}
            <Grid item xs={12}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                </Box>
          
              </Paper>
            </Grid>

            {/* User Distribution (Admin Only) */}
            {isAdmin && selectedUsers.length > 1 && (
              <Grid item xs={12}>
                <Paper sx={styles.chartCard}>
                  <Box sx={styles.chartCardHeader}>
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}

        {/* Tab 4: Advanced Analysis */}
        {activeTab === 3 && (
          <Grid container spacing={3}>
            {/* R Value Streak Analysis */}
            <Grid item xs={12}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <AutoGraphIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    רצף ומגמות בערכי R
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart
                      data={chartsData.cumulativeR}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis dataKey="tradeNumber" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="r" 
                        name="ערך R" 
                        stroke={theme.palette.info.main} 
                        dot={{ 
                          stroke: (entry) => entry.r > 0 ? theme.palette.success.main : theme.palette.error.main,
                          fill: (entry) => entry.r > 0 ? theme.palette.success.main : theme.palette.error.main,
                          r: 5
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="cumulative" 
                        name="ערך R מצטבר" 
                        stroke={theme.palette.primary.main} 
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            
            {/* R Correlation Analysis */}
            <Grid item xs={12} md={6}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <TroubleshootIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    מתאם בין משך עסקה לרווחיות
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={350}>
                    <ScatterChart
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis 
                        type="number" 
                        dataKey="duration" 
                        name="משך עסקה (דקות)" 
                        label={{ value: 'משך עסקה (דקות)', position: 'insideBottom', offset: -10 }} 
                        tickFormatter={(value) => formatDuration(value)}
                      />
                      <YAxis 
                        type="number" 
                        dataKey="r" 
                        name="ערך R" 
                        label={{ value: 'ערך R', angle: -90, position: 'insideLeft' }} 
                      />
                      <RechartsTooltip 
                        cursor={{ strokeDasharray: '3 3' }}
                        formatter={(value, name) => {
                          if (name === 'משך עסקה (דקות)') {
                            return [formatDuration(value), name];
                          }
                          return [value.toFixed(2), name];
                        }}
                      />
                      <Legend />
                      <Scatter 
                        name="עסקאות" 
                        data={chartsData.durationVsR} 
                        fill={theme.palette.primary.main}
                      >
                        {chartsData.durationVsR.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.r > 0 ? theme.palette.success.main : theme.palette.error.main} 
                          />
                        ))}
                      </Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            
            {/* Symbol Performance Over Time */}
            <Grid item xs={12} md={6}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <StackedBarChartIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    ביצועי סימבולים לאורך זמן
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={350}>
                    <LineChart
                      data={chartsData.cumulativeR}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis dataKey="tradeNumber" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      {Array.from(new Set(chartsData.cumulativeR.map(d => d.symbol))).map((symbol, index) => (
                        <Line 
                          key={symbol}
                          type="monotone" 
                          dataKey="r" 
                          name={symbol} 
                          stroke={COLORS[index % COLORS.length]} 
                          strokeWidth={2}
                          dot={{ r: 3 }}
                          connectNulls
                          data={chartsData.cumulativeR.filter(d => d.symbol === symbol)}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            
            {/* R Value Distribution */}
            <Grid item xs={12}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <AttachMoneyIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    התפלגות ערכי R (הצלחה מול כישלון)
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart
                      data={[
                        { name: '-1 R (הפסד)', value: stats.lossCount },
                        ...Array.from(
                          new Set(
                            stats.rValues
                              .filter(t => t.r > 0)
                              .map(t => Math.floor(t.r))
                          )
                        ).sort((a, b) => a - b).map(r => ({
                          name: `${r} R+`,
                          value: stats.rValues.filter(t => t.r >= r && t.r < r + 1).length
                        }))
                      ]}
                      margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis dataKey="name" />
                      <YAxis label={{ value: 'מספר עסקאות', angle: -90, position: 'insideLeft' }} />
                      <RechartsTooltip />
                      <Legend />
                      <Bar 
                        dataKey="value" 
                        name="מספר עסקאות" 
                        fill={theme.palette.primary.main}
                        barSize={60}
                      >
                        {[
                          { name: '-1 R (הפסד)', value: stats.lossCount },
                          ...Array.from(
                            new Set(
                              stats.rValues
                                .filter(t => t.r > 0)
                                .map(t => Math.floor(t.r))
                            )
                          ).sort((a, b) => a - b).map(r => ({
                            name: `${r} R+`,
                            value: stats.rValues.filter(t => t.r >= r && t.r < r + 1).length
                          }))
                        ].map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.name.includes('הפסד') ? theme.palette.error.main : theme.palette.success.main} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            {/* User Performance Comparison (Admin Only) */}
            {isAdmin && selectedUsers.length > 1 && (
              <Grid item xs={12}>
                <Paper sx={styles.chartCard}>
                  <Box sx={styles.chartCardHeader}>
                    <Typography variant="subtitle1" fontWeight="bold" color="secondary">
                      <CompareIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                      השוואת ביצועי משתמשים
                    </Typography>
                  </Box>
                  <Box sx={styles.chartCardContent}>
                    <ResponsiveContainer width="100%" height={400}>
                      <ComposedChart
                        data={chartsData.rByUser}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                        <XAxis dataKey="user" />
                        <YAxis yAxisId="left" orientation="left" label={{ value: 'ממוצע R', angle: -90, position: 'insideLeft' }} />
                        <YAxis yAxisId="right" orientation="right" domain={[0, 100]} label={{ value: 'אחוז הצלחה (%)', angle: 90, position: 'insideRight' }} />
                        <RechartsTooltip />
                        <Legend />
                        <Bar 
                          yAxisId="left" 
                          dataKey="avgR" 
                          name="ממוצע R" 
                          fill={theme.palette.secondary.main}
                          barSize={40}
                        >
                          {chartsData.rByUser.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={entry.avgR > 0 ? theme.palette.success.main : theme.palette.error.main} 
                            />
                          ))}
                        </Bar>
                        <Line 
                          yAxisId="right" 
                          type="monotone" 
                          dataKey="winRate" 
                          name="אחוז הצלחה" 
                          stroke={theme.palette.info.main}
                          strokeWidth={3}
                          dot={{ r: 6 }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}

        {/* Tab 5: Summary Tables */}
        {activeTab === 4 && (
          <Grid container spacing={3}>
            {/* Best Performing Trades */}
            <Grid item xs={12} md={6}>
              <Paper sx={styles.tableContainer}>
                <Box 
                  sx={{ 
                    py: 1.5, 
                    px: 2, 
                    bgcolor: alpha(theme.palette.success.main, 0.1), 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" color="success.main">
                    <ArrowUpwardIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    עסקאות המצטיינות (Top 5)
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={styles.tableHeader}>
                      <TableRow>
                        <TableCell sx={styles.tableHeaderCell}>תאריך</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>סימבול</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>סשן</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>טריגר</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>משך</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>יציאה</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>ערך R</TableCell>
                        {isAdmin && <TableCell sx={styles.tableHeaderCell}>משתמש</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats.rValues
                        .sort((a, b) => b.r - a.r)
                        .slice(0, 5)
                        .map((trade, index) => (
                          <TableRow 
                            key={trade.id}
                            hover
                            sx={{
                              '&:nth-of-type(even)': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                              },
                              transition: 'background-color 0.2s',
                              "&:hover": {
                                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                              }
                            }}
                          >
                            <TableCell>{trade.date}</TableCell>
                            <TableCell>{trade.symbol}</TableCell>
                            <TableCell>{trade.session}</TableCell>
                            <TableCell>{trade.matchedTrigger}</TableCell>
                            <TableCell>{formatDuration(trade.duration)}</TableCell>
                            <TableCell>{trade.tpMilestoneExit || '-'}</TableCell>
                            <TableCell 
                              sx={{ 
                                fontWeight: 'bold', 
                                color: theme.palette.success.main 
                              }}
                            >
                              +{trade.r.toFixed(2)}
                            </TableCell>
                            {isAdmin && <TableCell>{trade.userDisplayName || trade.userName}</TableCell>}
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            
            {/* Worst Performing Trades */}
            <Grid item xs={12} md={6}>
              <Paper sx={styles.tableContainer}>
                <Box 
                  sx={{ 
                    py: 1.5, 
                    px: 2, 
                    bgcolor: alpha(theme.palette.error.main, 0.1), 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" color="error.main">
                    <ArrowDownwardIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    עסקאות החלשות (Bottom 5)
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={styles.tableHeader}>
                      <TableRow>
                        <TableCell sx={styles.tableHeaderCell}>תאריך</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>סימבול</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>סשן</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>טריגר</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>משך</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>סיבת סטופ</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>ערך R</TableCell>
                        {isAdmin && <TableCell sx={styles.tableHeaderCell}>משתמש</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats.rValues
                        .sort((a, b) => a.r - b.r)
                        .slice(0, 5)
                        .map((trade, index) => (
                          <TableRow 
                            key={trade.id}
                            hover
                            sx={{
                              '&:nth-of-type(even)': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                              },
                              transition: 'background-color 0.2s',
                              "&:hover": {
                                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                              }
                            }}
                          >
                            <TableCell>{trade.date}</TableCell>
                            <TableCell>{trade.symbol}</TableCell>
                            <TableCell>{trade.session}</TableCell>
                            <TableCell>{trade.matchedTrigger}</TableCell>
                            <TableCell>{formatDuration(trade.duration)}</TableCell>
                            <TableCell>{trade.stopReason || '-'}</TableCell>
                            <TableCell 
                              sx={{ 
                                fontWeight: 'bold', 
                                color: theme.palette.error.main 
                              }}
                            >
                              {trade.r.toFixed(2)}
                            </TableCell>
                            {isAdmin && <TableCell>{trade.userDisplayName || trade.userName}</TableCell>}
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            
            {/* Symbol Performance Summary */}
            <Grid item xs={12} md={6}>
              <Paper sx={styles.tableContainer}>
                <Box 
                  sx={{ 
                    py: 1.5, 
                    px: 2, 
                    bgcolor: alpha(theme.palette.primary.main, 0.1), 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" color="primary.main">
                    <CurrencyBitcoinIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    טבלת ביצועים לפי סימבול
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={styles.tableHeader}>
                      <TableRow>
                        <TableCell sx={styles.tableHeaderCell}>סימבול</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>עסקאות</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>אחוז הצלחה</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>סה"כ R</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>ממוצע R</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(
                        realTrades.reduce((acc, trade) => {
                          const symbol = trade.symbol;
                          if (!acc[symbol]) {
                            acc[symbol] = {
                              count: 0,
                              wins: 0,
                              totalR: 0
                            };
                          }
                          
                          acc[symbol].count++;
                          if (trade.outcome === 'Take Profit') {
                            acc[symbol].wins++;
                          }
                          
                          // Calculate R
                          let r = -1;
                          if (trade.outcome === 'Take Profit') {
                            if (trade.tpMilestoneExit) {
                              const parts = trade.tpMilestoneExit.split(':');
                              if (parts.length === 2) {
                                r = parseFloat(parts[1]);
                              } else {
                                r = 1;
                              }
                            } else {
                              r = 1;
                            }
                          }
                          
                          acc[symbol].totalR += r;
                          
                          return acc;
                        }, {})
                      )
                        .map(([symbol, data]) => ({
                          symbol,
                          count: data.count,
                          winRate: (data.wins / data.count) * 100,
                          totalR: data.totalR,
                          avgR: data.totalR / data.count
                        }))
                        .sort((a, b) => b.avgR - a.avgR)
                        .map((row) => (
                          <TableRow 
                            key={row.symbol}
                            hover
                            sx={{
                              '&:nth-of-type(even)': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                              },
                              transition: 'background-color 0.2s',
                              "&:hover": {
                                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                              }
                            }}
                          >
                            <TableCell>{row.symbol}</TableCell>
                            <TableCell>{row.count}</TableCell>
                            <TableCell>{row.winRate.toFixed(1)}%</TableCell>
                            <TableCell 
                              sx={{ 
                                fontWeight: 'bold', 
                                color: row.totalR > 0 ? theme.palette.success.main : theme.palette.error.main 
                              }}
                            >
                              {row.totalR.toFixed(2)}
                            </TableCell>
                            <TableCell 
                              sx={{ 
                                fontWeight: 'bold', 
                                color: row.avgR > 0 ? theme.palette.success.main : theme.palette.error.main 
                              }}
                            >
                              {row.avgR.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            
            {/* Session Performance Summary */}
            <Grid item xs={12} md={6}>
              <Paper sx={styles.tableContainer}>
                <Box 
                  sx={{ 
                    py: 1.5, 
                    px: 2, 
                    bgcolor: alpha(theme.palette.primary.main, 0.1), 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" color="primary.main">
                    <AccessTimeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    טבלת ביצועים לפי סשן
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={styles.tableHeader}>
                      <TableRow>
                        <TableCell sx={styles.tableHeaderCell}>סשן</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>עסקאות</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>אחוז הצלחה</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>סה"כ R</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>ממוצע R</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(
                        realTrades.reduce((acc, trade) => {
                          const session = trade.session;
                          if (!acc[session]) {
                            acc[session] = {
                              count: 0,
                              wins: 0,
                              totalR: 0
                            };
                          }
                          
                          acc[session].count++;
                          if (trade.outcome === 'Take Profit') {
                            acc[session].wins++;
                          }
                          
                          // Calculate R
                          let r = -1;
                          if (trade.outcome === 'Take Profit') {
                            if (trade.tpMilestoneExit) {
                              const parts = trade.tpMilestoneExit.split(':');
                              if (parts.length === 2) {
                                r = parseFloat(parts[1]);
                              } else {
                                r = 1;
                              }
                            } else {
                              r = 1;
                            }
                          }
                          
                          acc[session].totalR += r;
                          
                          return acc;
                        }, {})
                      )
                        .map(([session, data]) => ({
                          session,
                          count: data.count,
                          winRate: (data.wins / data.count) * 100,
                          totalR: data.totalR,
                          avgR: data.totalR / data.count
                        }))
                        .sort((a, b) => b.avgR - a.avgR)
                        .map((row) => (
                          <TableRow 
                            key={row.session}
                            hover
                            sx={{
                              '&:nth-of-type(even)': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                              },
                              transition: 'background-color 0.2s',
                              "&:hover": {
                                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                              }
                            }}
                          >
                            <TableCell>{row.session}</TableCell>
                            <TableCell>{row.count}</TableCell>
                            <TableCell>{row.winRate.toFixed(1)}%</TableCell>
                            <TableCell 
                              sx={{ 
                                fontWeight: 'bold', 
                                color: row.totalR > 0 ? theme.palette.success.main : theme.palette.error.main 
                              }}
                            >
                              {row.totalR.toFixed(2)}
                            </TableCell>
                            <TableCell 
                              sx={{ 
                                fontWeight: 'bold', 
                                color: row.avgR > 0 ? theme.palette.success.main : theme.palette.error.main 
                              }}
                            >
                              {row.avgR.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            
            {/* Day Performance Summary */}
            <Grid item xs={12}>
              <Paper sx={styles.tableContainer}>
                <Box 
                  sx={{ 
                    py: 1.5, 
                    px: 2, 
                    bgcolor: alpha(theme.palette.primary.main, 0.1), 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" color="primary.main">
                    <CalendarTodayIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    טבלת ביצועים לפי יום בשבוע
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={styles.tableHeader}>
                      <TableRow>
                        <TableCell sx={styles.tableHeaderCell}>יום</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>עסקאות</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>רווחים</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>הפסדים</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>אחוז הצלחה</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>סה"כ R</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>ממוצע R</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(
                        realTrades.reduce((acc, trade) => {
                          const day = trade.dayOfWeek;
                          if (!acc[day]) {
                            acc[day] = {
                              count: 0,
                              wins: 0,
                              losses: 0,
                              totalR: 0
                            };
                          }
                          
                          acc[day].count++;
                          
                          if (trade.outcome === 'Take Profit') {
                            acc[day].wins++;
                          } else if (trade.outcome === 'Stop Loss') {
                            acc[day].losses++;
                          }
                          
                          // Calculate R
                          let r = -1;
                          if (trade.outcome === 'Take Profit') {
                            if (trade.tpMilestoneExit) {
                              const parts = trade.tpMilestoneExit.split(':');
                              if (parts.length === 2) {
                                r = parseFloat(parts[1]);
                              } else {
                                r = 1;
                              }
                            } else {
                              r = 1;
                            }
                          }
                          
                          acc[day].totalR += r;
                          
                          return acc;
                        }, {})
                      )
                        .map(([day, data]) => ({
                          day,
                          count: data.count,
                          wins: data.wins,
                          losses: data.losses,
                          winRate: (data.wins / data.count) * 100,
                          totalR: data.totalR,
                          avgR: data.totalR / data.count
                        }))
                        // Sort by order of day in week
                        .sort((a, b) => {
                          const dayOrder = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
                          return dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day);
                        })
                        .map((row) => (
                          <TableRow 
                            key={row.day}
                            hover
                            sx={{
                              '&:nth-of-type(even)': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                              },
                              transition: 'background-color 0.2s',
                              "&:hover": {
                                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                              }
                            }}
                          >
                            <TableCell>{row.day}</TableCell>
                            <TableCell>{row.count}</TableCell>
                            <TableCell sx={{ color: theme.palette.success.main }}>{row.wins}</TableCell>
                            <TableCell sx={{ color: theme.palette.error.main }}>{row.losses}</TableCell>
                            <TableCell>{row.winRate.toFixed(1)}%</TableCell>
                            <TableCell 
                              sx={{ 
                                fontWeight: 'bold', 
                                color: row.totalR > 0 ? theme.palette.success.main : theme.palette.error.main 
                              }}
                            >
                              {row.totalR.toFixed(2)}
                            </TableCell>
                            <TableCell 
                              sx={{ 
                                fontWeight: 'bold', 
                                color: row.avgR > 0 ? theme.palette.success.main : theme.palette.error.main 
                              }}
                            >
                              {row.avgR.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            {/* User Performance Summary (Admin Only) */}
            {isAdmin && selectedUsers.length > 1 && (
              <Grid item xs={12}>
                <Paper sx={styles.tableContainer}>
                  <Box 
                    sx={{ 
                      py: 1.5, 
                      px: 2, 
                      bgcolor: alpha(theme.palette.secondary.main, 0.1), 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold" color="secondary.main">
                      <GroupIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                      טבלת ביצועים לפי משתמש
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={styles.tableHeader}>
                        <TableRow>
                          <TableCell sx={styles.tableHeaderCell}>משתמש</TableCell>
                          <TableCell sx={styles.tableHeaderCell}>טריגר</TableCell>
                          <TableCell sx={styles.tableHeaderCell}>עסקאות</TableCell>
                          <TableCell sx={styles.tableHeaderCell}>רווחים</TableCell>
                          <TableCell sx={styles.tableHeaderCell}>הפסדים</TableCell>
                          <TableCell sx={styles.tableHeaderCell}>אחוז הצלחה</TableCell>
                          <TableCell sx={styles.tableHeaderCell}>סה"כ R</TableCell>
                          <TableCell sx={styles.tableHeaderCell}>ממוצע R</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {userStats.map((user) => (
                          <TableRow 
                            key={user.userId}
                            hover
                            sx={{
                              '&:nth-of-type(even)': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                              },
                              transition: 'background-color 0.2s',
                              "&:hover": {
                                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                              }
                            }}
                          >
                            <TableCell>{user.userName}</TableCell>
                            <TableCell>{user.trigger}</TableCell>
                            <TableCell>{user.totalTrades}</TableCell>
                            <TableCell sx={{ color: theme.palette.success.main }}>{user.winCount}</TableCell>
                            <TableCell sx={{ color: theme.palette.error.main }}>{user.lossCount}</TableCell>
                            <TableCell>{user.winRate.toFixed(1)}%</TableCell>
                            <TableCell 
                              sx={{ 
                                fontWeight: 'bold', 
                                color: user.totalR > 0 ? theme.palette.success.main : theme.palette.error.main 
                              }}
                            >
                              {user.totalR.toFixed(2)}
                            </TableCell>
                            <TableCell 
                              sx={{ 
                                fontWeight: 'bold', 
                                color: user.avgR > 0 ? theme.palette.success.main : theme.palette.error.main 
                              }}
                            >
                              {user.avgR.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}

        {/* Tab 6: Insights & Tips */}
        {activeTab === 5 && (
          <Grid container spacing={3}>
            {/* Best Performance Insights */}
            <Grid item xs={12} md={6}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <EmojiEventsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    תמצית הביצועים הטובים ביותר
                  </Typography>
                </Box>
                <Box sx={{ ...styles.chartCardContent, p: 3 }}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" color="primary" gutterBottom>
                      הסימבול הרווחי ביותר:
                    </Typography>
                    {chartsData.rBySymbol.length > 0 ? (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        p: 2,
                        borderRadius: 2
                      }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: theme.palette.success.main,
                            mr: 2
                          }}
                        >
                          <CurrencyBitcoinIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h5" fontWeight="bold">
                            {chartsData.rBySymbol.sort((a, b) => b.avgR - a.avgR)[0]?.symbol || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ממוצע של {chartsData.rBySymbol.sort((a, b) => b.avgR - a.avgR)[0]?.avgR.toFixed(2) || 0} R לעסקה
                            מתוך {chartsData.rBySymbol.sort((a, b) => b.avgR - a.avgR)[0]?.count || 0} עסקאות
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Typography>אין מספיק נתונים</Typography>
                    )}
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" color="primary" gutterBottom>
                      הסשן הרווחי ביותר:
                    </Typography>
                    {chartsData.rBySession.length > 0 ? (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        p: 2,
                        borderRadius: 2
                      }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: theme.palette.success.main,
                            mr: 2
                          }}
                        >
                          <AccessTimeIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h5" fontWeight="bold">
                            {chartsData.rBySession.sort((a, b) => b.avgR - a.avgR)[0]?.session || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ממוצע של {chartsData.rBySession.sort((a, b) => b.avgR - a.avgR)[0]?.avgR.toFixed(2) || 0} R לעסקה
                            מתוך {chartsData.rBySession.sort((a, b) => b.avgR - a.avgR)[0]?.count || 0} עסקאות
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Typography>אין מספיק נתונים</Typography>
                    )}
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" color="primary" gutterBottom>
                      הטריגר הרווחי ביותר:
                    </Typography>
                    {chartsData.rByTrigger.length > 0 ? (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        p: 2,
                        borderRadius: 2
                      }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: theme.palette.success.main,
                            mr: 2
                          }}
                        >
                          <TrendingUpIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h5" fontWeight="bold">
                            {chartsData.rByTrigger.sort((a, b) => b.avgR - a.avgR)[0]?.trigger || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ממוצע של {chartsData.rByTrigger.sort((a, b) => b.avgR - a.avgR)[0]?.avgR.toFixed(2) || 0} R לעסקה
                            מתוך {chartsData.rByTrigger.sort((a, b) => b.avgR - a.avgR)[0]?.count || 0} עסקאות
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Typography>אין מספיק נתונים</Typography>
                    )}
                  </Box>
                  
                  <Box>
                    <Typography variant="h6" color="primary" gutterBottom>
                      היום הרווחי ביותר:
                    </Typography>
                    {chartsData.rByDay.length > 0 ? (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        p: 2,
                        borderRadius: 2
                      }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: theme.palette.success.main,
                            mr: 2
                          }}
                        >
                          <CalendarTodayIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h5" fontWeight="bold">
                            {chartsData.rByDay.sort((a, b) => b.avgR - a.avgR)[0]?.day || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ממוצע של {chartsData.rByDay.sort((a, b) => b.avgR - a.avgR)[0]?.avgR.toFixed(2) || 0} R לעסקה
                            מתוך {chartsData.rByDay.sort((a, b) => b.avgR - a.avgR)[0]?.count || 0} עסקאות
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Typography>אין מספיק נתונים</Typography>
                    )}
                  </Box>

                  {/* Best User (Admin Only) */}
                  {isAdmin && selectedUsers.length > 1 && chartsData.rByUser.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" color="secondary" gutterBottom>
                        המשתמש המצטיין:
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        bgcolor: alpha(theme.palette.secondary.main, 0.1),
                        p: 2,
                        borderRadius: 2
                      }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: theme.palette.secondary.main,
                            mr: 2
                          }}
                        >
                          <PersonIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h5" fontWeight="bold">
                            {chartsData.rByUser.sort((a, b) => b.avgR - a.avgR)[0]?.user || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ממוצע של {chartsData.rByUser.sort((a, b) => b.avgR - a.avgR)[0]?.avgR.toFixed(2) || 0} R לעסקה
                            עם אחוז הצלחה של {chartsData.rByUser.sort((a, b) => b.avgR - a.avgR)[0]?.winRate.toFixed(1) || 0}%
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
            
            {/* Performance Improvement Tips */}
            <Grid item xs={12} md={6}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <AutoGraphIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    טיפים לשיפור הביצועים
                  </Typography>
                </Box>
                <Box sx={{ ...styles.chartCardContent, p: 3 }}>
                  {stats.totalR < 0 ? (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        p: 2,
                        mb: 2,
                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                        borderRadius: 2
                      }}>
                        <WarningIcon color="warning" sx={{ mr: 1 }} />
                        <Typography variant="subtitle1" fontWeight="medium">
                          שים לב! סך ה-R שלך שלילי. הנה כמה נקודות שכדאי לבדוק:
                        </Typography>
                      </Box>
                      
                      <Box component="ul" sx={{ pl: 2 }}>
                        <Box component="li" sx={{ mb: 1 }}>
                          <Typography>
                            <strong>יחס סיכוי/סיכון:</strong> ודא כי אתה נכנס לעסקאות רק כאשר הפוטנציאל לרווח גבוה משמעותית מהסיכון.
                          </Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 1 }}>
                          <Typography>
                            <strong>נקודות כניסה:</strong> בחן מחדש את אסטרטגיית הכניסה שלך. האם אתה מחכה לאישור מספק?
                          </Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 1 }}>
                          <Typography>
                            <strong>ניהול סיכונים:</strong> האם אתה מגדיר נקודות סטופ מתאימות? שקול להקטין את גודל הפוזיציה.
                          </Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 1 }}>
                          <Typography>
                            <strong>משמעת במסחר:</strong> האם אתה דבק באסטרטגיה שלך או פועל על בסיס אימפולסים?
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        p: 2,
                        mb: 2,
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        borderRadius: 2
                      }}>
                        <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                        <Typography variant="subtitle1" fontWeight="medium">
                          כל הכבוד! סך ה-R שלך חיובי. הנה כמה טיפים לשיפור נוסף:
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  
                  <Typography variant="h6" color="primary" gutterBottom>
                    ממצאים מרכזיים
                  </Typography>
                  
                  <Box component="ul" sx={{ pl: 2 }}>
                    {stats.winRate < 50 && (
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography>
                          <strong>אחוז הצלחה נמוך ({stats.winRate.toFixed(1)}%):</strong> שקול להתמקד יותר באיתות טכניים חזקים ולהמתין לאישור נוסף לפני כניסה לעסקה.
                        </Typography>
                      </Box>
                    )}
                    
                    {chartsData.rByTimeDistribution.some(item => item.avgR > 1.5) && (
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography>
                          <strong>שעות המסחר הטובות ביותר:</strong> הנתונים מראים שאתה מצליח יותר בשעות מסוימות. שקול למקד את המסחר שלך בשעות אלו.
                        </Typography>
                      </Box>
                    )}
                    
                    {chartsData.durationVsR.length > 0 && (
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography>
                          <strong>משך אופטימלי:</strong> נראה שעסקאות באורך {
                            formatDuration(chartsData.durationVsR
                              .filter(d => d.r > 0)
                              .sort((a, b) => b.r - a.r)
                              .slice(0, Math.max(3, Math.floor(chartsData.durationVsR.length / 10)))
                              .reduce((sum, item) => sum + item.duration, 0) /
                              Math.min(3, chartsData.durationVsR.filter(d => d.r > 0).length))
                          } בממוצע מניבות את התוצאות הטובות ביותר.
                        </Typography>
                      </Box>
                    )}
                    
                    {stats.profitFactor < 1.5 && stats.profitFactor > 0 && (
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography>
                          <strong>פקטור רווח נמוך ({stats.profitFactor.toFixed(2)}):</strong> שקול להגדיל את יחס הסיכוי/סיכון שלך או להיות יותר בררני בעסקאות.
                        </Typography>
                      </Box>
                    )}
                    
                    {chartsData.stopReasons.length > 0 && (
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography>
                          <strong>סיבות סטופ נפוצות:</strong> הסיבה השכיחה ביותר לסטופ היא "{chartsData.stopReasons.sort((a, b) => b.count - a.count)[0]?.reason}". התמקד בשיפור גורם זה.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  
                  <Typography variant="h6" color="primary" sx={{ mt: 3, mb: 1 }}>
                    המלצות להמשך
                  </Typography>
                  
                  <Box sx={{ 
                    p: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    borderRadius: 2
                  }}>
                    <Typography gutterBottom>
                      <strong>1. התמקד בחוזקות:</strong> המשך להתמקד ב{chartsData.rBySymbol.sort((a, b) => b.avgR - a.avgR)[0]?.symbol || 'סימבולים'} וסשן {chartsData.rBySession.sort((a, b) => b.avgR - a.avgR)[0]?.session || 'המועדף עליך'}.
                    </Typography>
                    
                    <Typography gutterBottom>
                      <strong>2. מזער חולשות:</strong> שקול להפחית מסחר ב{chartsData.rBySymbol.sort((a, b) => a.avgR - b.avgR)[0]?.symbol || 'סימבולים חלשים'} או לשפר את האסטרטגיה הספציפית.
                    </Typography>
                    
                    <Typography gutterBottom>
                      <strong>3. המשך לתעד:</strong> המשך לתעד את העסקאות שלך עם הערות מפורטות כדי לזהות דפוסים נוספים.
                    </Typography>
                    
                    <Typography>
                      <strong>4. ניתוח תקופתי:</strong> בצע ניתוח מעמיק זה מדי חודש כדי לעקוב אחר התקדמותך ולהתאים את האסטרטגיה שלך.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            
            {/* Performance Metrics Gauges - Extended */}
            <Grid item xs={12}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <SpeedIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    מדדי ביצוע מורחבים
                  </Typography>
                </Box>
                <Box sx={{ ...styles.chartCardContent, p: 3 }}>
                  <Grid container spacing={3}>
                    {/* Average Trade Duration */}
                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: alpha(theme.palette.primary.main, 0.05), 
                        borderRadius: 2,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center' 
                      }}>
                        <Box sx={{ position: 'relative', mb: 2 }}>
                          <TimerIcon sx={{ fontSize: 60, color: theme.palette.primary.main, opacity: 0.8 }} />
                        </Box>
                        <Typography variant="h6" color="primary" align="center" gutterBottom>
                          משך עסקה ממוצע
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" align="center">
                          {formatDuration(stats.averageDuration)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                          {stats.averageDuration < 30 ? 'סוחר מהיר' : 
                           stats.averageDuration < 120 ? 'זמן ממוצע' : 'סוחר ארוך טווח'}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Best Time of Day */}
                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: alpha(theme.palette.primary.main, 0.05), 
                        borderRadius: 2,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center' 
                      }}>
                        <Box sx={{ position: 'relative', mb: 2 }}>
                          <ScheduleIcon sx={{ fontSize: 60, color: theme.palette.success.main, opacity: 0.8 }} />
                        </Box>
                        <Typography variant="h6" color="primary" align="center" gutterBottom>
                          זמן המסחר האופטימלי
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" align="center">
                          {chartsData.rByTimeDistribution.length > 0 ? 
                            chartsData.rByTimeDistribution.sort((a, b) => b.avgR - a.avgR)[0]?.time || '-' : 
                            '-'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                          ממוצע של {chartsData.rByTimeDistribution.length > 0 ? 
                            chartsData.rByTimeDistribution.sort((a, b) => b.avgR - a.avgR)[0]?.avgR.toFixed(2) || '0' : 
                            '0'} R לעסקה
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Most Successful TP */}
                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: alpha(theme.palette.primary.main, 0.05), 
                        borderRadius: 2,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center' 
                      }}>
                        <Box sx={{ position: 'relative', mb: 2 }}>
                          <TrackChangesIcon sx={{ fontSize: 60, color: theme.palette.warning.main, opacity: 0.8 }} />
                        </Box>
                        <Typography variant="h6" color="primary" align="center" gutterBottom>
                          יעד TP מוצלח ביותר
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" align="center">
                          {chartsData.tpDistribution.length > 0 ?
                            chartsData.tpDistribution.sort((a, b) => b.count - a.count)[0]?.tp || '-' :
                            '-'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                          {chartsData.tpDistribution.length > 0 ?
                            `${chartsData.tpDistribution.sort((a, b) => b.count - a.count)[0]?.count || '0'} עסקאות מוצלחות` :
                            'אין נתונים מספיקים'}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    {/* Trade Frequency */}
                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: alpha(theme.palette.primary.main, 0.05), 
                        borderRadius: 2,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center' 
                      }}>
                        <Box sx={{ position: 'relative', mb: 2 }}>
                          <AlarmIcon sx={{ fontSize: 60, color: theme.palette.info.main, opacity: 0.8 }} />
                        </Box>
                        <Typography variant="h6" color="primary" align="center" gutterBottom>
                          תדירות מסחר
                        </Typography>
                        {stats.totalTrades > 0 && tradeDatesRange > 0 ? (
                          <>
                            <Typography variant="h4" fontWeight="bold" align="center">
                              {(stats.totalTrades / tradeDatesRange).toFixed(2)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                              עסקאות ליום מסחר
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary" align="center">
                            אין מספיק נתונים
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* R/Trade */}
                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: alpha(theme.palette.primary.main, 0.05), 
                        borderRadius: 2,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center' 
                      }}>
                        <Box sx={{ position: 'relative', mb: 2 }}>
                          <ShowChartIcon sx={{ fontSize: 60, color: theme.palette.success.main, opacity: 0.8 }} />
                        </Box>
                        <Typography variant="h6" color="primary" align="center" gutterBottom>
                          רווח ממוצע לעסקה
                        </Typography>
                        <Typography variant="h4" fontWeight="bold" align="center" color={stats.avgR > 0 ? 'success.main' : 'error.main'}>
                          {stats.avgR.toFixed(2)} R
                        </Typography>
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                          {stats.avgR > 0.5 ? 'מצוין' : stats.avgR > 0 ? 'טוב' : 'דורש שיפור'}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Consistency Score */}
                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: alpha(theme.palette.primary.main, 0.05), 
                        borderRadius: 2,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center' 
                      }}>
                        <Box sx={{ position: 'relative', mb: 2 }}>
                          <QueryStatsIcon sx={{ fontSize: 60, color: theme.palette.secondary.main, opacity: 0.8 }} />
                        </Box>
                        <Typography variant="h6" color="primary" align="center" gutterBottom>
                          ציון עקביות
                        </Typography>
                        {stats.rValues && stats.rValues.length > 0 ? (
                          <>
                            <Typography variant="h4" fontWeight="bold" align="center">
                              {calculateConsistencyScore(stats.rValues)}%
                            </Typography>
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                              ככל שהציון גבוה יותר, העקביות גבוהה יותר
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary" align="center">
                            אין מספיק נתונים
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* Simulated TP Performance (Admin Only) */}
                    {isAdmin && tpMilestoneFilter !== 'all' && (
                      <Grid item xs={12}>
                        <Box sx={{ 
                          p: 2, 
                          bgcolor: alpha(theme.palette.secondary.main, 0.05), 
                          borderRadius: 2,
                          mt: 2,
                          border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`
                        }}>
                          <Typography variant="h6" color="secondary" gutterBottom>
                            <TrackChangesIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                            סימולציית ביצועים לפי {tpMilestoneFilter}
                          </Typography>
                          
                          <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} md={3}>
                              <Paper sx={{ p: 2 }}>
                                <Typography variant="body2" color="text.secondary">אחוז הצלחה</Typography>
                                <Typography variant="h5" fontWeight="bold" color={simulatedStats.winRate > stats.winRate ? 'success.main' : 'text.primary'}>
                                  {simulatedStats.winRate.toFixed(1)}%
                                </Typography>
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  mt: 1,
                                  color: simulatedStats.winRate > stats.winRate ? 'success.main' : 
                                         simulatedStats.winRate < stats.winRate ? 'error.main' : 'text.secondary'
                                }}>
                                  {simulatedStats.winRate > stats.winRate ? 
                                    <ArrowUpwardIcon fontSize="small" /> : 
                                    simulatedStats.winRate < stats.winRate ? 
                                    <ArrowDownwardIcon fontSize="small" /> : 
                                    <span>-</span>}
                                  <Typography variant="body2" sx={{ ml: 0.5 }}>
                                    {(Math.abs(simulatedStats.winRate - stats.winRate)).toFixed(1)}%
                                  </Typography>
                                </Box>
                              </Paper>
                            </Grid>
                            
                            <Grid item xs={12} md={3}>
                              <Paper sx={{ p: 2 }}>
                                <Typography variant="body2" color="text.secondary">ממוצע R</Typography>
                                <Typography variant="h5" fontWeight="bold" color={simulatedStats.avgR > stats.avgR ? 'success.main' : 'text.primary'}>
                                  {simulatedStats.avgR.toFixed(2)}
                                </Typography>
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  mt: 1,
                                  color: simulatedStats.avgR > stats.avgR ? 'success.main' : 
                                         simulatedStats.avgR < stats.avgR ? 'error.main' : 'text.secondary'
                                }}>
                                  {simulatedStats.avgR > stats.avgR ? 
                                    <ArrowUpwardIcon fontSize="small" /> : 
                                    simulatedStats.avgR < stats.avgR ? 
                                    <ArrowDownwardIcon fontSize="small" /> : 
                                    <span>-</span>}
                                  <Typography variant="body2" sx={{ ml: 0.5 }}>
                                    {(Math.abs(simulatedStats.avgR - stats.avgR)).toFixed(2)}
                                  </Typography>
                                </Box>
                              </Paper>
                            </Grid>
                            
                            <Grid item xs={12} md={3}>
                              <Paper sx={{ p: 2 }}>
                                <Typography variant="body2" color="text.secondary">פקטור רווח</Typography>
                                <Typography variant="h5" fontWeight="bold" color={simulatedStats.profitFactor > stats.profitFactor ? 'success.main' : 'text.primary'}>
                                  {simulatedStats.profitFactor.toFixed(2)}
                                </Typography>
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  mt: 1,
                                  color: simulatedStats.profitFactor > stats.profitFactor ? 'success.main' : 
                                         simulatedStats.profitFactor < stats.profitFactor ? 'error.main' : 'text.secondary'
                                }}>
                                  {simulatedStats.profitFactor > stats.profitFactor ? 
                                    <ArrowUpwardIcon fontSize="small" /> : 
                                    simulatedStats.profitFactor < stats.profitFactor ? 
                                    <ArrowDownwardIcon fontSize="small" /> : 
                                    <span>-</span>}
                                  <Typography variant="body2" sx={{ ml: 0.5 }}>
                                    {(Math.abs(simulatedStats.profitFactor - stats.profitFactor)).toFixed(2)}
                                  </Typography>
                                </Box>
                              </Paper>
                            </Grid>
                            
                            <Grid item xs={12} md={3}>
                              <Paper sx={{ p: 2 }}>
                                <Typography variant="body2" color="text.secondary">תוחלת</Typography>
                                <Typography variant="h5" fontWeight="bold" color={simulatedStats.expectancy > stats.expectancy ? 'success.main' : 'text.primary'}>
                                  {simulatedStats.expectancy.toFixed(2)}
                                </Typography>
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  mt: 1,
                                  color: simulatedStats.expectancy > stats.expectancy ? 'success.main' : 
                                         simulatedStats.expectancy < stats.expectancy ? 'error.main' : 'text.secondary'
                                }}>
                                  {simulatedStats.expectancy > stats.expectancy ? 
                                    <ArrowUpwardIcon fontSize="small" /> : 
                                    simulatedStats.expectancy < stats.expectancy ? 
                                    <ArrowDownwardIcon fontSize="small" /> : 
                                    <span>-</span>}
                                  <Typography variant="body2" sx={{ ml: 0.5 }}>
                                    {(Math.abs(simulatedStats.expectancy - stats.expectancy)).toFixed(2)}
                                  </Typography>
                                </Box>
                              </Paper>
                            </Grid>
                          </Grid>
                          
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            * הסימולציה מראה מה היו התוצאות אם היית יוצא מכל העסקאות בנקודת {tpMilestoneFilter} (במקום בנקודות היציאה המקוריות)
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Paper>
            </Grid>
            
            {/* Performance Metrics Gauges */}
            <Grid item xs={12}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    <SpeedIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    מדדי ביצוע מרכזיים
                  </Typography>
                </Box>
                <Box sx={{ ...styles.chartCardContent, p: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="primary" gutterBottom>
                          אחוז הצלחה
                        </Typography>
                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                          <CircularProgress
                            variant="determinate"
                            value={100}
                            size={140}
                            thickness={4}
                            sx={{ color: alpha(theme.palette.divider, 0.2) }}
                          />
                          <CircularProgress
                            variant="determinate"
                            value={Math.min(stats.winRate, 100)}
                            size={140}
                            thickness={4}
                            sx={{
                              color: stats.winRate > 50 ? theme.palette.success.main : 
                                    stats.winRate > 40 ? theme.palette.warning.main : 
                                    theme.palette.error.main,
                              position: 'absolute',
                              left: 0,
                            }}
                          />
                          <Box
                            sx={{
                              top: 0,
                              left: 0,
                              bottom: 0,
                              right: 0,
                              position: 'absolute',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography variant="h4" fontWeight="bold">
                              {stats.winRate.toFixed(1)}%
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            יעד מומלץ: 40-60%
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={stats.winRate > 60 ? 100 : stats.winRate < 40 ? 0 : ((stats.winRate - 40) / 20) * 100}
                          sx={{ 
                            mt: 1, 
                            height: 8, 
                            borderRadius: 4,
                            bgcolor: alpha(theme.palette.divider, 0.2)
                          }} 
                        />
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="primary" gutterBottom>
                        פקטור רווח
                      </Typography>
                      <Box sx={{ position: 'relative', display: 'inline-block' }}>
                        <CircularProgress
                          variant="determinate"
                          value={100}
                          size={140}
                          thickness={4}
                          sx={{ color: alpha(theme.palette.divider, 0.2) }}
                        />
                        <CircularProgress
                          variant="determinate"
                          value={Math.min(stats.profitFactor / 3 * 100, 100)}
                          size={140}
                          thickness={4}
                          sx={{
                            color: stats.profitFactor > 2 ? theme.palette.success.main : 
                                  stats.profitFactor > 1 ? theme.palette.warning.main : 
                                  theme.palette.error.main,
                            position: 'absolute',
                            left: 0,
                          }}
                        />
                        <Box
                          sx={{
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            position: 'absolute',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography variant="h4" fontWeight="bold">
                            {stats.profitFactor.toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          יעד מומלץ: מעל 1.5
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={stats.profitFactor > 3 ? 100 : stats.profitFactor < 1 ? 0 : ((stats.profitFactor - 1) / 2) * 100}
                          sx={{ 
                            mt: 1, 
                            height: 8, 
                            borderRadius: 4,
                            bgcolor: alpha(theme.palette.divider, 0.2)
                          }} 
                        />
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="primary" gutterBottom>
                        תוחלת
                      </Typography>
                      <Box sx={{ position: 'relative', display: 'inline-block' }}>
                        <CircularProgress
                          variant="determinate"
                          value={100}
                          size={140}
                          thickness={4}
                          sx={{ color: alpha(theme.palette.divider, 0.2) }}
                        />
                        <CircularProgress
                          variant="determinate"
                          value={stats.expectancy < 0 ? 0 : Math.min(stats.expectancy / 0.5 * 100, 100)}
                          size={140}
                          thickness={4}
                          sx={{
                            color: stats.expectancy > 0.3 ? theme.palette.success.main : 
                                  stats.expectancy > 0 ? theme.palette.warning.main : 
                                  theme.palette.error.main,
                            position: 'absolute',
                            left: 0,
                          }}
                        />
                        <Box
                          sx={{
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            position: 'absolute',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography variant="h4" fontWeight="bold">
                            {stats.expectancy.toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          יעד מומלץ: מעל 0.2
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={stats.expectancy > 0.5 ? 100 : stats.expectancy < 0 ? 0 : (stats.expectancy / 0.5) * 100}
                          sx={{ 
                            mt: 1, 
                            height: 8, 
                            borderRadius: 4,
                            bgcolor: alpha(theme.palette.divider, 0.2)
                          }} 
                        />
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="primary" gutterBottom>
                        ממוצע R
                      </Typography>
                      <Box sx={{ position: 'relative', display: 'inline-block' }}>
                        <CircularProgress
                          variant="determinate"
                          value={100}
                          size={140}
                          thickness={4}
                          sx={{ color: alpha(theme.palette.divider, 0.2) }}
                        />
                        <CircularProgress
                          variant="determinate"
                          value={stats.avgR < 0 ? 0 : Math.min(stats.avgR / 1 * 100, 100)}
                          size={140}
                          thickness={4}
                          sx={{
                            color: stats.avgR > 0.5 ? theme.palette.success.main : 
                                  stats.avgR > 0 ? theme.palette.warning.main : 
                                  theme.palette.error.main,
                            position: 'absolute',
                            left: 0,
                          }}
                        />
                        <Box
                          sx={{
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            position: 'absolute',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography variant="h4" fontWeight="bold">
                            {stats.avgR.toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          יעד מומלץ: מעל 0.3
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={stats.avgR > 1 ? 100 : stats.avgR < 0 ? 0 : stats.avgR / 1 * 100}
                          sx={{ 
                            mt: 1, 
                            height: 8, 
                            borderRadius: 4,
                            bgcolor: alpha(theme.palette.divider, 0.2)
                          }} 
                        />
                      </Box>
                    </Box>
                  </Grid>
                 </Grid>
                 </Box>
                </Paper>
            </Grid>
          </Grid>
        )}

        {/* Tab 7: Admin Analysis */}
        {isAdmin && activeTab === 6 && (
          <Grid container spacing={3}>
            {/* User Comparison Chart */}
            <Grid item xs={12}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="secondary">
                    <CompareIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    השוואת ביצועי משתמשים
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={500}>
                    <ComposedChart
                      data={userStats}
                      margin={{ top: 20, right: 30, left: 30, bottom: 100 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis 
                        dataKey="userName" 
                        angle={-45} 
                        textAnchor="end" 
                        height={80} 
                        interval={0}
                      />
                      <YAxis 
                        yAxisId="left" 
                        orientation="left" 
                        label={{ value: 'Average R', angle: -90, position: 'insideLeft' }} 
                      />
                      <YAxis 
                        yAxisId="right" 
                        orientation="right" 
                        domain={[0, 100]} 
                        label={{ value: 'Win Rate (%)', angle: 90, position: 'insideRight' }} 
                      />
                      <RechartsTooltip content={<UserComparisonTooltip />} />
                      <Legend />
                      <Bar 
                        yAxisId="left" 
                        dataKey="avgR" 
                        name="ממוצע R" 
                        fill={theme.palette.secondary.main}
                        barSize={50}
                      >
                        {userStats.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.avgR > 0 ? theme.palette.success.main : theme.palette.error.main} 
                          />
                        ))}
                      </Bar>
                      <Line 
                        yAxisId="right" 
                        type="monotone" 
                        dataKey="winRate" 
                        name="אחוז הצלחה" 
                        stroke={theme.palette.primary.main}
                        strokeWidth={3}
                        dot={{ r: 6 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            {/* User Performance Table */}
            <Grid item xs={12}>
              <Paper sx={styles.tableContainer}>
                <Box 
                  sx={{ 
                    py: 1.5, 
                    px: 2, 
                    bgcolor: alpha(theme.palette.secondary.main, 0.1), 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" color="secondary.main">
                    <LeaderboardIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    טבלת דירוג משתמשים
                  </Typography>
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead sx={styles.tableHeader}>
                      <TableRow>
                        <TableCell sx={styles.tableHeaderCell}>דירוג</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>משתמש</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>מייל</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>טריגר</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>שעות מסחר</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>עסקאות</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>אחוז הצלחה</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>סה"כ R</TableCell>
                        <TableCell sx={styles.tableHeaderCell}>ממוצע R</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {userStats.map((user, index) => (
                        <TableRow 
                          key={user.userId}
                          hover
                          sx={{
                            bgcolor: index === 0 ? alpha(theme.palette.success.main, 0.1) : 
                                    index === userStats.length - 1 ? alpha(theme.palette.error.main, 0.05) :
                                    index % 2 === 0 ? alpha(theme.palette.primary.main, 0.02) : 'transparent',
                            transition: 'background-color 0.2s',
                            "&:hover": {
                              backgroundColor: alpha(theme.palette.primary.main, 0.05),
                            }
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {index === 0 ? (
                                <EmojiEventsIcon sx={{ mr: 1, color: theme.palette.warning.main }} />
                              ) : (
                                <Typography variant="body1" fontWeight="bold" sx={{ mr: 1 }}>
                                  {index + 1}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ mr: 1, bgcolor: COLORS[index % COLORS.length] }}>
                                {user.userName.charAt(0).toUpperCase()}
                              </Avatar>
                              <Typography fontWeight="medium">
                                {user.userName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <Chip 
                              label={user.trigger} 
                              size="small" 
                              color="secondary"
                            />
                          </TableCell>
                          <TableCell>{user.tradingHours}</TableCell>
                          <TableCell>{user.totalTrades}</TableCell>
                          <TableCell sx={{ 
                            fontWeight: 'bold',
                            color: user.winRate > 50 ? theme.palette.success.main : 
                                  user.winRate > 40 ? theme.palette.warning.main : 
                                  theme.palette.error.main
                          }}>
                            {user.winRate.toFixed(1)}%
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 'bold',
                            color: user.totalR > 0 ? theme.palette.success.main : theme.palette.error.main
                          }}>
                            {user.totalR.toFixed(2)}
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 'bold',
                            color: user.avgR > 0 ? theme.palette.success.main : theme.palette.error.main
                          }}>
                            {user.avgR.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            {/* Trigger Performance Analysis */}
            <Grid item xs={12} md={6}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="secondary">
                    <SignalCellularAltIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    ביצועים לפי טריגר
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={Array.from(availableTriggers)
                        .filter(trigger => trigger !== 'all')
                        .map(trigger => {
                          const usersWithTrigger = allUsers.filter(u => u.trigger === trigger);
                          const userIds = usersWithTrigger.map(u => u.id);
                          const relevantTrades = realTrades.filter(t => userIds.includes(t.uid));
                          const winCount = relevantTrades.filter(t => t.outcome === 'Take Profit').length;
                          const lossCount = relevantTrades.filter(t => t.outcome === 'Stop Loss').length;
                          
                          // Helper function to calculate R
                          const getTradeR = (trade) => {
                            if (trade.outcome === 'Stop Loss') return -1;
                            if (trade.outcome === 'Take Profit') {
                              if (trade.tpMilestoneExit) {
                                const parts = trade.tpMilestoneExit.split(':');
                                if (parts.length === 2) {
                                  return parseFloat(parts[1]);
                                }
                              }
                              return 1;
                            }
                            return 0;
                          };
                          
                          const totalR = relevantTrades.reduce((sum, trade) => sum + getTradeR(trade), 0);
                          
                          return {
                            trigger,
                            userCount: usersWithTrigger.length,
                            tradeCount: relevantTrades.length,
                            winRate: relevantTrades.length > 0 ? (winCount / relevantTrades.length) * 100 : 0,
                            avgR: relevantTrades.length > 0 ? totalR / relevantTrades.length : 0
                          };
                        })}
                      margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis 
                        dataKey="trigger" 
                        angle={-45} 
                        textAnchor="end" 
                        height={80}
                      />
                      <YAxis label={{ value: 'ממוצע R', angle: -90, position: 'insideLeft' }} />
                      <RechartsTooltip />
                      <Legend />
                      <Bar 
                        dataKey="avgR" 
                        name="ממוצע R" 
                        fill={theme.palette.secondary.main}
                        barSize={40}
                      >
                        {availableTriggers
                          .filter(trigger => trigger !== 'all')
                          .map((trigger, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={COLORS[index % COLORS.length]} 
                            />
                          ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            {/* Trading Hours Performance */}
            <Grid item xs={12} md={6}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="secondary">
                    <AccessTimeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    ביצועים לפי שעות מסחר
                  </Typography>
                </Box>
                <Box sx={styles.chartCardContent}>
                  <ResponsiveContainer width="100%" height={400}>
                    <ComposedChart
                      data={Array.from(new Set(allUsers.map(u => u.tradingHours)))
                        .filter(hours => hours && hours !== 'לא מוגדר')
                        .map(hours => {
                          const usersWithHours = allUsers.filter(u => u.tradingHours === hours);
                          const userIds = usersWithHours.map(u => u.id);
                          const relevantTrades = realTrades.filter(t => userIds.includes(t.uid));
                          const winCount = relevantTrades.filter(t => t.outcome === 'Take Profit').length;
                          
                          // Helper function to calculate R
                          const getTradeR = (trade) => {
                            if (trade.outcome === 'Stop Loss') return -1;
                            if (trade.outcome === 'Take Profit') {
                              if (trade.tpMilestoneExit) {
                                const parts = trade.tpMilestoneExit.split(':');
                                if (parts.length === 2) {
                                  return parseFloat(parts[1]);
                                }
                              }
                              return 1;
                            }
                            return 0;
                          };
                          
                          const totalR = relevantTrades.reduce((sum, trade) => sum + getTradeR(trade), 0);
                          
                          return {
                            hours,
                            userCount: usersWithHours.length,
                            tradeCount: relevantTrades.length,
                            winRate: relevantTrades.length > 0 ? (winCount / relevantTrades.length) * 100 : 0,
                            avgR: relevantTrades.length > 0 ? totalR / relevantTrades.length : 0
                          };
                        })}
                      margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis 
                        dataKey="hours" 
                        angle={-45} 
                        textAnchor="end" 
                        height={80}
                      />
                      <YAxis yAxisId="left" orientation="left" label={{ value: 'ממוצע R', angle: -90, position: 'insideLeft' }} />
                      <YAxis yAxisId="right" orientation="right" domain={[0, 100]} label={{ value: 'אחוז הצלחה (%)', angle: 90, position: 'insideRight' }} />
                      <RechartsTooltip />
                      <Legend />
                      <Bar 
                        yAxisId="left" 
                        dataKey="avgR" 
                        name="ממוצע R" 
                        fill={theme.palette.warning.main}
                        barSize={40}
                      />
                      <Line 
                        yAxisId="right" 
                        type="monotone" 
                        dataKey="winRate" 
                        name="אחוז הצלחה" 
                        stroke={theme.palette.success.main}
                        strokeWidth={3}
                        dot={{ r: 6 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            {/* Top Performers By Category */}
            <Grid item xs={12}>
              <Paper sx={styles.chartCard}>
                <Box sx={styles.chartCardHeader}>
                  <Typography variant="subtitle1" fontWeight="bold" color="secondary">
                    <EmojiEventsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    המצטיינים לפי קטגוריה
                  </Typography>
                </Box>
                <Box sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: alpha(theme.palette.success.main, 0.1), 
                        borderRadius: 2, 
                        height: '100%' 
                      }}>
                        <Typography variant="subtitle1" fontWeight="bold" color="success.main" gutterBottom>
                          יחס סיכוי/סיכון הגבוה ביותר
                        </Typography>
                        {userStats.length > 0 ? (
                          <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Avatar sx={{ mr: 2, bgcolor: theme.palette.success.main }}>
                                {userStats.sort((a, b) => b.avgR - a.avgR)[0]?.userName.charAt(0).toUpperCase() || '?'}
                              </Avatar>
                              <Box>
                                <Typography variant="h6" fontWeight="bold">
                                  {userStats.sort((a, b) => b.avgR - a.avgR)[0]?.userName || 'לא נמצא'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {userStats.sort((a, b) => b.avgR - a.avgR)[0]?.email || ''}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                ממוצע R:
                              </Typography>
                              <Typography variant="h5" color="success.main" fontWeight="bold">
                                {userStats.sort((a, b) => b.avgR - a.avgR)[0]?.avgR.toFixed(2) || '0.00'}
                              </Typography>
                            </Box>
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                טריגר:
                              </Typography>
                              <Chip 
                                label={userStats.sort((a, b) => b.avgR - a.avgR)[0]?.trigger || 'לא ידוע'} 
                                color="secondary" 
                                size="small" 
                                sx={{ mt: 0.5 }} 
                              />
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="body1" color="text.secondary">
                            אין מספיק נתונים
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: alpha(theme.palette.info.main, 0.1), 
                        borderRadius: 2, 
                        height: '100%' 
                      }}>
                        <Typography variant="subtitle1" fontWeight="bold" color="info.main" gutterBottom>
                          אחוז ההצלחה הגבוה ביותר
                        </Typography>
                        {userStats.length > 0 ? (
                          <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Avatar sx={{ mr: 2, bgcolor: theme.palette.info.main }}>
                                {userStats.sort((a, b) => b.winRate - a.winRate)[0]?.userName.charAt(0).toUpperCase() || '?'}
                              </Avatar>
                              <Box>
                                <Typography variant="h6" fontWeight="bold">
                                  {userStats.sort((a, b) => b.winRate - a.winRate)[0]?.userName || 'לא נמצא'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {userStats.sort((a, b) => b.winRate - a.winRate)[0]?.email || ''}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                אחוז הצלחה:
                              </Typography>
                              <Typography variant="h5" color="info.main" fontWeight="bold">
                                {userStats.sort((a, b) => b.winRate - a.winRate)[0]?.winRate.toFixed(1) || '0.0'}%
                              </Typography>
                            </Box>
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                טריגר:
                              </Typography>
                              <Chip 
                                label={userStats.sort((a, b) => b.winRate - a.winRate)[0]?.trigger || 'לא ידוע'} 
                                color="secondary" 
                                size="small" 
                                sx={{ mt: 0.5 }} 
                              />
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="body1" color="text.secondary">
                            אין מספיק נתונים
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: alpha(theme.palette.warning.main, 0.1), 
                        borderRadius: 2, 
                        height: '100%' 
                      }}>
                        <Typography variant="subtitle1" fontWeight="bold" color="warning.main" gutterBottom>
                          כמות העסקאות הגבוהה ביותר
                        </Typography>
                        {userStats.length > 0 ? (
                          <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Avatar sx={{ mr: 2, bgcolor: theme.palette.warning.main }}>
                                {userStats.sort((a, b) => b.totalTrades - a.totalTrades)[0]?.userName.charAt(0).toUpperCase() || '?'}
                              </Avatar>
                              <Box>
                                <Typography variant="h6" fontWeight="bold">
                                  {userStats.sort((a, b) => b.totalTrades - a.totalTrades)[0]?.userName || 'לא נמצא'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {userStats.sort((a, b) => b.totalTrades - a.totalTrades)[0]?.email || ''}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                מספר עסקאות:
                              </Typography>
                              <Typography variant="h5" color="warning.main" fontWeight="bold">
                                {userStats.sort((a, b) => b.totalTrades - a.totalTrades)[0]?.totalTrades || '0'}
                              </Typography>
                            </Box>
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                טריגר:
                              </Typography>
                              <Chip 
                                label={userStats.sort((a, b) => b.totalTrades - a.totalTrades)[0]?.trigger || 'לא ידוע'} 
                                color="secondary" 
                                size="small" 
                                sx={{ mt: 0.5 }} 
                              />
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="body1" color="text.secondary">
                            אין מספיק נתונים
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default Stats;